import { useConfig } from 'providers/ConfigProvider';
import { useAuth } from "providers/AuthProvider";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaAngleDown, FaFileUpload} from 'react-icons/fa';
import { PiCheckBold, PiDownloadSimple, PiInfo, PiTrash, PiUploadSimple } from 'react-icons/pi';
import { motion } from 'framer-motion';
import axios from "axios";
import Select from "react-select";
import { Link } from 'react-router-dom';
import PhoneInput from 'elements/input/PhoneInput';
import TextInput from 'elements/input/TextInput';
import isEqual from "lodash/isEqual";

const getErrorMessage = (properties, key) => {
  return properties?.[key]?.[0]?.message || null;
};

const normalizeDateInputValue = (value) => {
  if (!value) return '';

  const datePart = String(value).match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (datePart) return datePart;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
};

const FORM_ACTION_BASE = 'inline-flex min-h-12 max-w-full items-center justify-center gap-2 whitespace-normal rounded-xl px-5 py-3 text-center text-sm font-bold leading-snug shadow-sm transition duration-200 focus-visible:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-50';
const FORM_ACTION_PRIMARY = `${FORM_ACTION_BASE} gradient-green text-white hover:-translate-y-0.5 hover:brightness-110 hover:shadow-lg focus-visible:ring-green-200`;
const FORM_ACTION_SECONDARY = `${FORM_ACTION_BASE} border border-green-200 bg-white text-green-700 hover:-translate-y-0.5 hover:border-green-300 hover:bg-green-50 focus-visible:ring-green-100`;
const FORM_ACTION_DANGER = `${FORM_ACTION_BASE} border border-red-200 bg-white text-red-600 hover:-translate-y-0.5 hover:border-red-300 hover:bg-red-50 focus-visible:ring-red-100`;
const FORM_UPLOAD_DROPZONE = 'flex min-w-0 flex-col items-center gap-4 rounded-2xl border-2 border-dashed p-4 text-center transition sm:p-5';
const FORM_UPLOAD_ACTIONS = 'flex w-full min-w-0 flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center';
const getFormUploadDropzoneState = (dragging) => dragging
  ? 'border-lime-500 bg-lime-50 shadow-md'
  : 'border-green-300 bg-white/80';
const getFormUploadIconState = (dragging) => dragging
  ? 'bg-lime-100 text-lime-700'
  : 'bg-green-50 text-green-700';

const getFormChoiceClassName = (selected, registrationVariant) => {
  if (!registrationVariant) {
    return selected
      ? 'border-gray-600 gradient-green text-white ring-4 ring-white/20'
      : 'border-white/30 bg-white/10 text-white hover:border-white/60 hover:bg-white/20';
  }

  return selected
    ? 'border-green-700 gradient-green text-white ring-4 ring-green-100 shadow-md'
    : 'border-gray-200 bg-slate-50 text-neutral-700 shadow-sm hover:border-green-400 hover:bg-green-50 hover:text-green-800';
};

function FormChoice({ name, value, selected, onChange, icon, children, registrationVariant, disabled = false }) {
  return (
    <label className={`block min-w-0 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected}
        onChange={onChange}
        disabled={disabled}
        className="peer sr-only"
      />
      <div
        className={`flex min-h-12 w-full min-w-0 items-center justify-center gap-2 rounded-xl border-2 px-3 py-3 text-center text-sm font-bold leading-tight transition duration-200 peer-focus-visible:outline-none peer-focus-visible:ring-4 peer-focus-visible:ring-green-200 ${
          disabled ? '' : 'hover:-translate-y-0.5 hover:shadow-md'
        } ${getFormChoiceClassName(selected, registrationVariant)}`}
      >
        {icon}
        <span className="min-w-0 break-words">{children}</span>
      </div>
    </label>
  );
}

function FormBlock(props) {
  const surfaceClassName = props.variant === 'registration'
    ? 'registration-card form-card rounded-3xl border border-gray-200 bg-white p-5 text-neutral-800 shadow-xl sm:p-6 lg:p-8'
    : 'form-card rounded-3xl bg-black/20 p-5 sm:p-6';
  const leadClassName = props.variant === 'registration' ? 'text-gray-600' : 'text-white';

  return (
    <div className={`flex w-full min-w-0 flex-col gap-5 font-display ${surfaceClassName} ${props.className || ''}`}>
      {(props.title || props.lead) && (
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex w-full min-w-0 flex-row items-start gap-3">
            {props.icon && (
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-green-50 text-2xl text-green-600">
                {props.icon}
              </span>
            )}
            {props.title && (
              <h2 className={`min-w-0 break-words font-black leading-tight ${props.titleColor} text-2xl md:text-3xl`}>
                {props.title}
              </h2>
            )}
          </div>
          {props.lead && (
            <p className={`${leadClassName} max-w-4xl text-sm leading-relaxed md:text-base`}>{props.lead}</p>
          )}
        </div>
      )}

      <div className='form-card-body flex min-w-0 flex-col gap-4 text-base md:text-lg'>{props.children}</div>
    </div>
  );
}

function CheckboxMark({ checked, error, disabled, variant, borderColor, backgroundColor }) {
  if (variant !== 'registration') {
    return (
      <span
        aria-hidden="true"
        className={`flex size-6 shrink-0 items-center justify-center rounded-full border-4 transition-all ${
          checked ? `${borderColor} bg-black/10` : 'border-gray-500 bg-black/10'
        }`}
      >
        {checked && <span className={`size-3 ${backgroundColor} rounded-full`} />}
      </span>
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`flex size-6 shrink-0 items-center justify-center rounded-md border-2 transition ${
        checked
          ? 'border-green-600 bg-green-600 text-white'
          : error
            ? 'border-red-500 bg-white text-transparent'
            : 'border-gray-300 bg-white text-transparent'
      } ${disabled ? 'opacity-50' : ''}`}
    >
      <PiCheckBold className="text-sm" />
    </span>
  );
}

export function PersonalForm(props) {
  const [serialNumber, setSerialNumber] = useState(props.value?.serialNumber || '-');

  const initialBirthDate = normalizeDateInputValue(props.value?.birthDate);
  const [birthDate, setBirthDate] = useState(initialBirthDate);
  const [birthDateError, setBirthDateError] = useState(null);
  const [birthYear, setBirthYear] = useState(initialBirthDate.slice(0, 4));
  const [birthMonth, setBirthMonth] = useState(initialBirthDate.slice(5, 7));
  const [birthDay, setBirthDay] = useState(initialBirthDate.slice(8, 10));
  const birthMonthRef = useRef(null);
  const birthDayRef = useRef(null);

  const [firstName, setFirstName] = useState(props.value?.firstName || '');
  const [firstNameError, setFirstNameError] = useState(null);

  const [lastName, setLastName] = useState(props.value?.lastName || '');
  const [lastNameError, setLastNameError] = useState(null);

  const [gender, setGender] = useState(props.value?.gender || "");
  const [genderError, setGenderError] = useState(null);

  useEffect(() => {
    props.onValueChange({ value: { birthDate, firstName, lastName, gender } });
  }, [birthDate, firstName, lastName, gender]);

  useEffect(() => {
      setGender(props.value?.gender || ""); 
  }, [props.value]);

  useEffect(() => {
    console.log('PersonalForm error useEffect triggered');
    if (getErrorMessage(props.error, 'birthDate')) setBirthDateError(getErrorMessage(props.error, 'birthDate'));
    if (getErrorMessage(props.error, 'firstName')) setFirstNameError(getErrorMessage(props.error, 'firstName'));
    if (getErrorMessage(props.error, 'lastName')) setLastNameError(getErrorMessage(props.error, 'lastName'));
    if (getErrorMessage(props.error, 'gender')) setGenderError(getErrorMessage(props.error, 'gender'));
  }, [props.error]);

  useEffect(() => { setBirthDateError(null); console.log('birthDate changed'); }, [birthDate]);
  useEffect(() => { setFirstNameError(null); console.log('firstName changed'); }, [firstName]);
  useEffect(() => { setLastNameError(null); console.log('lastName changed'); }, [lastName]);
  useEffect(() => { setGenderError(null); console.log('gender changed'); }, [gender]);

  useEffect(() => {
    if (birthYear.length !== 4 || birthMonth.length < 1 || birthDay.length < 1) {
      setBirthDate('');
      return;
    }

    const year = Number(birthYear);
    const month = Number(birthMonth);
    const day = Number(birthDay);
    const normalizedMonth = birthMonth.padStart(2, '0');
    const normalizedDay = birthDay.padStart(2, '0');
    const date = new Date(year, month - 1, day);
    const isValidDate = year >= 1900
      && year <= new Date().getFullYear()
      && date.getFullYear() === year
      && date.getMonth() === month - 1
      && date.getDate() === day;

    setBirthDate(isValidDate ? `${birthYear}-${normalizedMonth}-${normalizedDay}` : '');
  }, [birthYear, birthMonth, birthDay]);

  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const options = { year: 'numeric', month: 'long', day: 'numeric' }; 
    return date.toLocaleDateString('hu-HU', options);
  };

  const updateDatePart = (value, maxLength, setter, nextInputRef) => {
    const numericValue = value.replace(/\D/g, '').slice(0, maxLength);
    setter(numericValue);

    if (numericValue.length === maxLength && nextInputRef) {
      window.requestAnimationFrame(() => nextInputRef.current?.focus());
    }
  };

  const datePartsComplete = birthYear.length === 4
    && birthMonth.length >= 1
    && birthDay.length >= 1;
  const invalidDate = datePartsComplete && !birthDate;

  return (
    <FormBlock {...props} title="Személyes adataim" lead="Pár fontos adat, rólad." titleColor={props.color}>
      {props.edit && (
        <div className="flex flex-col gap-2">
          <p className={`font-black ${props.color}`}>
            Részjegysorszámom
          </p>
          <p className="text-base text-white/80">
            Az adatot a Fürge Diák tölti ki szerződéskötéskor, és nem szerkeszthető.
          </p>
          <input
            disabled={props.edit}
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            className="input w-full rounded-xl p-2 sm:max-w-sm"
            placeholder="Részjegysorszámom"
          />
        </div>
      )}
      <div className='grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-3'>
        <div className="form-field">
          <p className={`font-black ${props.color}`}>Születési dátumom</p>
          <div className="grid grid-cols-[minmax(5rem,1.5fr)_minmax(3.5rem,1fr)_minmax(3.5rem,1fr)] gap-2" role="group" aria-label="Születési dátum">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="bday-year"
              aria-label="Születési év"
              placeholder="ÉÉÉÉ"
              maxLength={4}
              disabled={props.edit}
              value={birthYear}
              onChange={(event) => updateDatePart(event.target.value, 4, setBirthYear, birthMonthRef)}
              className={`input min-w-0 w-full rounded-xl p-2 text-center ${birthDateError || invalidDate ? 'border-red-500' : ''}`}
            />
            <input
              ref={birthMonthRef}
              type="text"
              inputMode="numeric"
              autoComplete="bday-month"
              aria-label="Születési hónap"
              placeholder="HH"
              maxLength={2}
              disabled={props.edit}
              value={birthMonth}
              onChange={(event) => updateDatePart(event.target.value, 2, setBirthMonth, birthDayRef)}
              onBlur={(event) => event.currentTarget.value && setBirthMonth(event.currentTarget.value.padStart(2, '0'))}
              className={`input min-w-0 w-full rounded-xl p-2 text-center ${birthDateError || invalidDate ? 'border-red-500' : ''}`}
            />
            <input
              ref={birthDayRef}
              type="text"
              inputMode="numeric"
              autoComplete="bday-day"
              aria-label="Születési nap"
              placeholder="NN"
              maxLength={2}
              disabled={props.edit}
              value={birthDay}
              onChange={(event) => updateDatePart(event.target.value, 2, setBirthDay)}
              onBlur={(event) => event.currentTarget.value && setBirthDay(event.currentTarget.value.padStart(2, '0'))}
              className={`input min-w-0 w-full rounded-xl p-2 text-center ${birthDateError || invalidDate ? 'border-red-500' : ''}`}
            />
          </div>
          {!props.edit && birthDate && (
            <p className={props.variant === 'registration' ? 'text-sm text-neutral-500' : 'text-white/70'}>
              {formatDateForDisplay(birthDate)}
            </p>
          )}
          {invalidDate && <p className="text-red-500 text-xs">Adj meg egy érvényes dátumot.</p>}
          {birthDateError && <p className="text-red-500 text-xs">{birthDateError}</p>}
        </div>
        <div className="form-field">
          <p className={`font-black ${props.color}`}>Vezetéknevem</p>
          <input
            type="text"
            disabled={props.edit}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={`input w-full min-w-0 rounded-xl p-2 ${lastNameError ? 'border-red-500' : ''}`}
            placeholder="Vezetéknevem"
          />
          {lastNameError && <p className="text-red-500 text-xs">{lastNameError}</p>}
        </div>
        <div className="form-field">
          <p className={`font-black ${props.color}`}>Keresztnevem</p>
          <input
            type="text"
            disabled={props.edit}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={`input w-full min-w-0 rounded-xl p-2 ${firstNameError ? 'border-red-500' : ''}`}
            placeholder="Keresztnevem"
          />
          {firstNameError && <p className="text-red-500 text-xs">{firstNameError}</p>}
        </div>
      </div>
      <div className="form-field w-full sm:max-w-md">
        <p className={`font-black ${props.color}`}>Nemem</p>
        <div className="grid grid-cols-2 gap-3">
          <FormChoice
            name="gender"
            value="FEMALE"
            selected={gender === "FEMALE"}
            onChange={(e) => setGender(e.target.value)}
            registrationVariant={props.variant === 'registration'}
            icon={(
              <img
                src="/assets/images/icons/gal_gray.svg"
                alt=""
                aria-hidden="true"
                className={`size-6 shrink-0 ${gender === "FEMALE" ? 'brightness-0 invert' : ''}`}
              />
            )}
          >
            Lány
          </FormChoice>
          <FormChoice
            name="gender"
            value="MALE"
            selected={gender === "MALE"}
            onChange={(e) => setGender(e.target.value)}
            registrationVariant={props.variant === 'registration'}
            icon={(
              <img
                src="/assets/images/icons/guy_gray.svg"
                alt=""
                aria-hidden="true"
                className={`size-6 shrink-0 ${gender === "MALE" ? 'brightness-0 invert' : ''}`}
              />
            )}
          >
            Fiú
          </FormChoice>
        </div>
        {genderError && <p className="text-red-500 text-xs mt-2">{genderError}</p>}
      </div>
    </FormBlock>
  );
}

export function StudyForm(props) {
    const [collegeStudent, setCollegeStudent] = useState(props.value?.collegeStudent || null);
    const [collegeStudentError, setCollegeStudentError] = useState(null);

    const [activeSemester, setActiveSemester] = useState(props.value?.activeSemester || null);
    const [activeSemesterError, setActiveSemesterError] = useState(null);

    const handleCollegeStudentChange = (value) => {
      setCollegeStudent(value);
      if (!value) setActiveSemester(true);
    };

    useEffect(() => {
      props.onValueChange({ value: { collegeStudent, activeSemester } });
    }, [collegeStudent, activeSemester]);

    useEffect(() => {
      setCollegeStudent(props.value?.collegeStudent ?? null);
      setActiveSemester(props.value?.activeSemester ?? null);
    }, [props.value]);  
  
    useEffect(() => {
        if (getErrorMessage(props.error, 'collegeStudent')) {
            setCollegeStudentError(getErrorMessage(props.error, 'collegeStudent'));
        }
        if (getErrorMessage(props.error, 'activeSemester')) {
            setActiveSemesterError(getErrorMessage(props.error, 'activeSemester'));
        }
    }, [props.error]);

    useEffect(() => { setCollegeStudentError(null); }, [collegeStudent]);
    useEffect(() => { setActiveSemesterError(null); }, [activeSemester]);

    return (
        <FormBlock {...props} title="Tanulmányaim" lead="Jelenleg hol folytatod tanulmányaidat?" titleColor={props.color}>
          <div className="form-field-grid">
              <div className="form-field">
                  <label className={`block font-black ${props.color}`}>
                      Tanulmányi státusz
                  </label>

                  <div className="grid w-full grid-cols-2 gap-3">
                      <FormChoice
                          name="collegeStudent"
                          value="false"
                          selected={collegeStudent === false}
                          onChange={() => handleCollegeStudentChange(false)}
                          registrationVariant={props.variant === 'registration'}
                      >
                          Középiskola
                      </FormChoice>

                      <FormChoice
                          name="collegeStudent"
                          value="true"
                          selected={collegeStudent === true}
                          onChange={() => handleCollegeStudentChange(true)}
                          registrationVariant={props.variant === 'registration'}
                      >
                          Felsőoktatás
                      </FormChoice>
                  </div>

                  {collegeStudentError && (
                      <p className="text-red-500 text-xs">{collegeStudentError}</p>
                  )}
              </div>

              {collegeStudent && (
                  <div className="form-field">
                      <label className={`block font-black ${props.color}`}>
                          Jelenlegi félév státusza
                      </label>

                      <div className="grid w-full grid-cols-2 gap-3">
                          <FormChoice
                              name="activeSemester"
                              value="true"
                              selected={activeSemester === true}
                              onChange={() => setActiveSemester(true)}
                              registrationVariant={props.variant === 'registration'}
                          >
                              Aktív
                          </FormChoice>

                          <FormChoice
                              name="activeSemester"
                              value="false"
                              selected={activeSemester === false}
                              onChange={() => setActiveSemester(false)}
                              registrationVariant={props.variant === 'registration'}
                          >
                              Passzív
                          </FormChoice>
                      </div>

                      {activeSemesterError && (
                          <p className="text-red-500 text-xs">{activeSemesterError}</p>
                      )}
                  </div>
              )}
          </div>
        </FormBlock>
    );
}

export function SchoolForm(props) {
    const { getCombo } = useConfig(); 
    const [schoolName, setSchoolName] = useState(props.value?.schoolName || '');
    const [schoolNameError, setSchoolNameError] = useState(null);

    const [schoolSettlement, setSchoolSettlement] = useState(props.value?.schoolSettlement || '');
    const [schoolSettlementError, setSchoolSettlementError] = useState(null);

    const [schoolSpecialization, setSchoolSpecialization] = useState(props.value?.schoolSpecialization || '');
    const [schoolSpecializationError, setSchoolSpecializationError] = useState(null);

    const [specialty, setSpecialty] = useState(props.value?.specialty || '');
    const [specialtyError, setSpecialtyError] = useState(null);

    useEffect(() => {
      props.onValueChange({ value: { schoolName, schoolSettlement, schoolSpecialization, specialty } });
    }, [schoolName, schoolSettlement, schoolSpecialization, specialty]);

    useEffect(() => {
        setSchoolName(props.value?.schoolName || '');
        setSchoolSettlement(props.value?.schoolSettlement || '');
        setSchoolSpecialization(props.value?.schoolSpecialization || '');
        setSpecialty(props.value?.specialty || '');
    }, [props.value]);
    

    useEffect(() => {
      if (getErrorMessage(props.error, 'schoolName')) {
        setSchoolNameError(getErrorMessage(props.error, 'schoolName'));
      }
      if (getErrorMessage(props.error, 'schoolSettlement')) {
        setSchoolSettlementError(getErrorMessage(props.error, 'schoolSettlement'));
      }
      if (getErrorMessage(props.error, 'schoolSpecialization')) {
        setSchoolSpecializationError(getErrorMessage(props.error, 'schoolSpecialization'));
      }
      if (getErrorMessage(props.error, 'specialty')) {
        setSpecialtyError(getErrorMessage(props.error, 'specialty'));
      }
    }, [props.error]);

    useEffect(() => { setSchoolNameError(null); }, [schoolName]);
    useEffect(() => { setSchoolSettlementError(null); }, [schoolSettlement]);
    useEffect(() => { setSchoolSpecializationError(null); }, [schoolSpecialization]);
    useEffect(() => { setSpecialtyError(null); }, [specialty]);

    return (
        <FormBlock {...props} title="Iskolai adataim" lead="Jelenleg melyik intézményben folytatod a tanulmányaidat?" titleColor={props.color}>
          <div className='form-field-grid'>
            <div className='contents'>
              <div className="form-field">
                  <label className={`block font-black ${props.color}`} htmlFor="schoolName">Iskola neve</label>
                  <input
                      id='schoolName'
                      type="text"
                      value={schoolName}
                      onChange={(e) => setSchoolName(e.target.value)}
                      className={`input w-full min-w-0 rounded-xl px-4 py-2 ${schoolNameError ? 'border-red-500' : ''}`}
                      placeholder="Iskola neve"
                  />
                  {schoolNameError && <p className="text-red-500 text-xs">{schoolNameError}</p>}
              </div>

              <div className="form-field">
                  <label className={`block font-black ${props.color}`} htmlFor="schoolSettlement">Iskola települése</label>
                  <select
                      id='schoolSettlement'
                      value={schoolSettlement}
                      onChange={(e) => setSchoolSettlement(e.target.value)}
                      className={`input w-full min-w-0 rounded-xl px-4 py-2 ${schoolSettlementError ? 'border-red-500' : ''}`}
                  >
                      <option value="">Válaszd ki a települést</option>
                      {getCombo('settlement')?.filter((s) => s.primary === true).map((settlement) => (
                          <option key={settlement.id} value={settlement.id}>
                              {settlement.name}
                          </option>
                      ))}
                  </select>
                  {schoolSettlementError && <p className="text-red-500 text-xs">{schoolSettlementError}</p>}
              </div>
            </div>

            <div className='contents'>
              <div className="form-field">
                  <label className={`block font-black ${props.color}`} htmlFor="schoolSpecialization">Szakirány</label>
                  <select
                      id='schoolSpecialization'
                      value={schoolSpecialization}
                      onChange={(e) => setSchoolSpecialization(e.target.value)}
                      className={`input w-full min-w-0 rounded-xl px-4 py-2 ${schoolSpecializationError ? 'border-red-500' : ''}`}
                  >
                      <option value="">Válaszd ki a szakirányt</option>
                      {getCombo('specialization')?.map((specialization) => (
                          <option key={specialization.id} value={specialization.id}>
                              {specialization.name}
                          </option>
                      ))}
                  </select>
                  {schoolSpecializationError && <p className="text-red-500 text-xs">{schoolSpecializationError}</p>}
              </div>

              <div className="form-field">
                  <label className={`block font-black ${props.color}`} htmlFor="specialty">Szak</label>
                  <input
                      id="specialty"
                      type="text"
                      value={specialty}
                      onChange={(e) => setSpecialty(e.target.value)}
                      className={`input w-full min-w-0 rounded-xl px-4 py-2 ${specialtyError ? 'border-red-500' : ''}`}
                      placeholder="Szak"
                  />
                  {specialtyError && <p className="text-red-500 text-xs">{specialtyError}</p>}
              </div>
            </div>
          </div>
        </FormBlock>
    );
}

export function ContactForm(props) {
  const [phoneNumber, setPhoneNumber] = useState(
    props.value?.serviceProviderNumber || props.value?.phoneNumber
      ? { serviceProviderNumber: props.value?.serviceProviderNumber ?? "", phoneNumber: props.value?.phoneNumber ?? "" }
      : { serviceProviderNumber: "", phoneNumber: "" }
  );

  const [email, setEmail] = useState(props.value?.email || "");

  const [alternativePhoneNumbers, setAlternativePhoneNumbers] = useState(props.value?.alternativePhoneNumberList || []);

  const [deletedAltIds, setDeletedAltIds] = useState([]);

  useEffect(() => {
    setDeletedAltIds([]);
    setAlternativePhoneNumbers(props.value?.alternativePhoneNumberList || []);
    setEmail(props.value?.email || "");
    setPhoneNumber({
      serviceProviderNumber: props.value?.serviceProviderNumber ?? "",
      phoneNumber: props.value?.phoneNumber ?? "",
    });
  }, [props.value]);

  const normalizeErr = (e) => {
    if (!e) return null;
    if (Array.isArray(e)) return e.join(" ");
    if (typeof e === "string") return e;
    if (typeof e === "object") return e.message ?? JSON.stringify(e);
    return String(e);
  };

  const [phoneNumberError, setPhoneNumberError] = useState(null);
  const [emailError, setEmailError] = useState(null);

  const getFieldErrorMessage = (fieldError) => {
    if (!fieldError) return null;

    if (Array.isArray(fieldError)) {
      const msgs = fieldError.map((e) => {
        if (e?.key === "InvalidEmailAddressFormat") {
          return "Hibás a forma!";
        }

        if (e?.message) return e.message;

        return null;
      }).filter(Boolean);

      return msgs.length > 0 ? msgs.join(" ") : null;
    }

    if (typeof fieldError === "object") {
      if (fieldError.key === "InvalidEmailAddressFormat") {
        return "Hibás a forma!";
      }

      if (fieldError.message) {
        return fieldError.message;
      }

      return null;
    }

    return String(fieldError);
  };

  useEffect(() => {
    if (!props.error) {
      setPhoneNumberError(null);
      setEmailError(null);
      return;
    }

    const phoneErr =
      props.error.phoneNumber || props.error.serviceProviderNumber || null;
    const emailErr = props.error.email || null;

    setPhoneNumberError(getFieldErrorMessage(phoneErr));
    setEmailError(getFieldErrorMessage(emailErr));
  }, [props.error]);

  useEffect(() => {
      const dto = {
          email,
          phoneNumber: phoneNumber?.phoneNumber?.trim() || null,
          serviceProviderNumber: phoneNumber?.serviceProviderNumber || null,
          countryCode: 1,
          contactPercent: false,
          contractingPercent: false,
          cvUploaded: false,
          doqTermsAccepted: false,
          drivingPercent: false,
          hasCar: false,
          languagePercent: false,
          photoPercent: false,
          schoolPercent: false,
          studiesPercent: false,
          updatedAddress: false,
          updatedBirthDate: false,
          updatedDOQId: false,
          updatedEmail: false,
          updatedGoldcardID: false,
          updatedStudentCardNumber: false,
          updatedTaxId: false,
          updatedTempAddress: false
      };

      const kept = (alternativePhoneNumbers || [])
        .map(({ _tmpId, ...rest }) => rest)
        .filter((x) => (x.phoneNumber ?? "").trim().length > 0);

      const deletedMarkers = (deletedAltIds || []).map((id) => ({ id }));

      const payload = {
          altPNDtoList: {
            alternativePhoneNumbers: [...kept, ...deletedMarkers],
          },
          dto: dto
      };

      props.onValueChange(payload);
  }, [email, phoneNumber, alternativePhoneNumbers, deletedAltIds]);

  const removeAlt = (el) => {
    if (el.id == null) {
      setAlternativePhoneNumbers((prev) =>
        prev.filter((x) => (x.id ?? x._tmpId) !== (el.id ?? el._tmpId))
      );
      return;
    }

    setDeletedAltIds((prev) => (prev.includes(el.id) ? prev : [...prev, el.id]));
    setAlternativePhoneNumbers((prev) => prev.filter((x) => x.id !== el.id));
  };

  return (
      <FormBlock
          {...props}
          title="Kapcsolattartási adataim"
          lead="Add meg a kapcsolattartási adataidat hogy kapcsolatba tudjunk lépni veled."
          titleColor={props.color}
      >
          <div className="form-field-grid">
          <PhoneInput
              error={phoneNumberError}
              value={phoneNumber}
              onValueChange={(v) => setPhoneNumber(v ?? { serviceProviderNumber: "", phoneNumber: "" })}
              color={props.color}
              variant={props.variant}
              xlassName="order-1"
              placeholder="Telefonszám"
          />
          {props.edit && (
              <AlternativePhoneNumberList
                  color={props.color}
                  xlassName="order-3 lg:col-span-2"
                  value={alternativePhoneNumbers}
                  onValueChange={setAlternativePhoneNumbers}
                  onRemoveItem={removeAlt}
              />
          )}
          <TextInput
              error={emailError}
              value={email}
              onChangeText={setEmail}
              type="email"
              color={props.color}
              xlassName="order-2"
              placeholder="E-mail címem"
          />
          </div>
      </FormBlock>
  );
}

function AlternativePhoneNumberList(props) {
  const raw = props.value || [];

  const list = raw.map((x) => ({
    ...x,
    _tmpId: x._tmpId ?? crypto.randomUUID(),
  }));

  const addNew = () => {
    props.onValueChange([
      ...list,
      { _tmpId: crypto.randomUUID(), phoneNumber: "" },
    ]);
  };

  const update = (item, nextValue) => {
    const key = item.id ?? item._tmpId;
    props.onValueChange(
      list.map((x) => ((x.id ?? x._tmpId) === key ? { ...nextValue, _tmpId: x._tmpId } : x))
    );
  };

  return (
      <div className={`flex flex-col gap-2 ${props.xlassName || ""}`}>
          <p className={`font-black text-lg ${props.color}`}>Alternatív telefonszámaim</p>

          {list.map((item) => (
              <AlternativePhoneNumberSubForm
                key={item.id ?? item._tmpId}
                value={item}
                onValueChange={(v) => update(item, v)}
                onRemove={() => props.onRemoveItem?.(item)}
              />
          ))}

          {list.length <= 3 && (
              <button
                  type="button"
                  className={`${FORM_ACTION_PRIMARY} w-full sm:w-auto`}
                  onClick={addNew}
              >
                  Új telefonszám felvétele (+)
              </button>
          )}
      </div>
  );
}

function AlternativePhoneNumberSubForm(props) {
  return (
      <div className="grid grid-cols-1 gap-3 rounded-2xl bg-black/20 p-4 shadow-sm md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
          <input
              className="w-full min-w-0 rounded-xl border border-gray-300 p-2 md:flex-1"
              value={props.value?.phoneNumber ?? ""}
              onChange={(e) => props.onValueChange({ ...props.value, phoneNumber: e.target.value })}
              placeholder="Telefonszám"
          />
          <button
              type="button"
              className={`${FORM_ACTION_DANGER} w-full md:w-auto`}
              onClick={props.onRemove}
          >
              Telefonszám törlése (-)
          </button>
      </div>
  );
}

export function AddressFormBlock(props) {
  const { getCombo } = useConfig(); 

  const [open, setOpen] = useState(props.open);

  const [country, setCountry] = useState(
    props.value && props.value[props.fieldNameMap["country"]] || null
  );
  const [countryError, setCountryError] = useState(null);

  const [zipCode, setZipCode] = useState(
    props.value && props.value[props.fieldNameMap["zipCode"]] || ""
  );
  const [zipCodeError, setZipCodeError] = useState(null);

  const [settlement, setSettlement] = useState(
    props.value && props.value[props.fieldNameMap["settlement"]] || null
  );
  const [settlementError, setSettlementError] = useState(null);

  const [roadName, setRoadName] = useState(
    props.value && props.value[props.fieldNameMap["roadName"]] || ""
  );
  const [roadNameError, setRoadNameError] = useState(null);

  const [typesOfRoad, setTypesOfRoad] = useState(
    props.value && props.value[props.fieldNameMap["typesOfRoad"]] || null
  );
  const [typesOfRoadError, setTypesOfRoadError] = useState(null);

  const [houseNumber, setHouseNumber] = useState(
    props.value && props.value[props.fieldNameMap["houseNumber"]] || ""
  );
  const [houseNumberError, setHouseNumberError] = useState(null);

  const [building, setBuilding] = useState(
    props.value && props.value[props.fieldNameMap["building"]] || ""
  );
  const [buildingError, setBuildingError] = useState(null);

  const [stairway, setStairway] = useState(
    props.value && props.value[props.fieldNameMap["stairway"]] || ""
  );
  const [stairwayError, setStairwayError] = useState(null);

  const [floor, setFloor] = useState(
    props.value && props.value[props.fieldNameMap["floor"]] || ""
  );
  const [floorError, setFloorError] = useState(null);

  const [door, setDoor] = useState(
    props.value && props.value[props.fieldNameMap["door"]] || ""
  );
  const [doorError, setDoorError] = useState(null);

  const didMountRef = useRef(false);

  const getFieldErrorMessage = (fieldError) => {
    if (!fieldError) return null;

    if (Array.isArray(fieldError)) {
      const msgs = fieldError
        .map((e) => {
          if (e?.key === "UserModification.EmptyFields" && !e?.message) {
            return "Kötelező megadni!";
          }
          return e?.message ?? null;
        })
        .filter(Boolean);

      return msgs.length > 0 ? msgs.join(" ") : null;
    }

    if (typeof fieldError === "object") {
      if (fieldError.key === "UserModification.EmptyFields" && !fieldError.message) {
        return "Kötelező megadni!";
      }
      return fieldError.message ?? null;
    }

    return String(fieldError);
  };

  useEffect(() => {
    if (!props.error) {
      setCountryError(null);
      setZipCodeError(null);
      setSettlementError(null);
      setRoadNameError(null);
      setTypesOfRoadError(null);
      setHouseNumberError(null);
      setBuildingError(null);
      setStairwayError(null);
      setFloorError(null);
      setDoorError(null);
      return;
    }

    const errFor = (logicalField) => getFieldErrorMessage(props.error[props.fieldNameMap[logicalField]]);

    setCountryError(errFor("country"));
    setZipCodeError(errFor("zipCode"));
    setSettlementError(errFor("settlement"));
    setRoadNameError(errFor("roadName"));
    setTypesOfRoadError(errFor("typesOfRoad"));
    setHouseNumberError(errFor("houseNumber"));
    setBuildingError(errFor("building"));
    setStairwayError(errFor("stairway"));
    setFloorError(errFor("floor"));
    setDoorError(errFor("door"));
  }, [props.error, props.fieldNameMap]);

  useEffect(() => {
      didMountRef.current = false;
      setCountry(props.value?.[props.fieldNameMap["country"]] || null);
      setZipCode(props.value?.[props.fieldNameMap["zipCode"]] || "");
      setSettlement(props.value?.[props.fieldNameMap["settlement"]] || null);
      setRoadName(props.value?.[props.fieldNameMap["roadName"]] || "");
      setTypesOfRoad(props.value?.[props.fieldNameMap["typesOfRoad"]] || null);
      setHouseNumber(props.value?.[props.fieldNameMap["houseNumber"]] || "");
      setBuilding(props.value?.[props.fieldNameMap["building"]] || "");
      setStairway(props.value?.[props.fieldNameMap["stairway"]] || "");
      setFloor(props.value?.[props.fieldNameMap["floor"]] || "");
      setDoor(props.value?.[props.fieldNameMap["door"]] || "");
  }, [props.value]); 

  useEffect(() => {
    const newValue = {
      country,
      zipCode,
      settlement,
      roadName,
      typesOfRoad,
      houseNumber,
      building,
      stairway,
      floor,
      door,
    };

    const currentValue = {
      country: props.value?.[props.fieldNameMap["country"]] ?? null,
      zipCode: props.value?.[props.fieldNameMap["zipCode"]] ?? "",
      settlement: props.value?.[props.fieldNameMap["settlement"]] ?? null,
      roadName: props.value?.[props.fieldNameMap["roadName"]] ?? "",
      typesOfRoad: props.value?.[props.fieldNameMap["typesOfRoad"]] ?? null,
      houseNumber: props.value?.[props.fieldNameMap["houseNumber"]] ?? "",
      building: props.value?.[props.fieldNameMap["building"]] ?? "",
      stairway: props.value?.[props.fieldNameMap["stairway"]] ?? "",
      floor: props.value?.[props.fieldNameMap["floor"]] ?? "",
      door: props.value?.[props.fieldNameMap["door"]] ?? "",
    };

    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (!isEqual(newValue, currentValue)) {
      props.onValueChange?.(newValue);
    }
  }, [
    country,
    zipCode,
    settlement,
    roadName,
    typesOfRoad,
    houseNumber,
    building,
    stairway,
    floor,
    door,
    props.value,
    props.fieldNameMap,
  ]);

  useEffect(() => {
    if (settlement) return;

    if (zipCode && zipCode.length >= 4) {
      const settlements = getCombo("settlement") || [];
      const match = settlements.find((x) => String(x.zipCode) === String(zipCode));
      if (match) setSettlement(match.id);
    }
  }, [zipCode, settlement, getCombo]);

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
  };

  const handleZipChange = (e) => {
    const v = e.target.value;
    setZipCode(v);

    setSettlement(null);
  };

  const handleSelectChange = (setter) => (event) => {
    setter(event.target.value);
  };

  const toggleOpen = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  return (
    <div className={`flex min-w-0 flex-col gap-4 ${props.xlassName || ""}`}>
      {(props.title || props.lead) && (
        <div
          className="flex min-w-0 cursor-pointer flex-row gap-4 rounded-2xl bg-black/20 p-4 sm:p-5"
          onClick={toggleOpen}
        >
          <div className="flex items-center">
            <span
                className={`transform transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"}`}
            >
                <FaAngleDown className={`w-8 h-8 ${props.color}`} />
            </span>
          </div>
          <div>
            <h3 className={`font-bold ${props.color}`}>{props.title}</h3>
            {props.lead && <p className="text-white text-sm">{props.lead}</p>}
          </div>
        </div>
      )}

      {open && (
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 sm:px-4">
          <div>
            <select
              value={country || ""}
              onChange={handleSelectChange(setCountry)}
              className={`w-full input px-4 py-2 rounded-xl ${countryError ? "border-red-500" : ""}`}
            >
              <option value="">
                Ország
              </option>
              {getCombo("country")?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            {countryError && <p className="text-red-500 text-xs">{countryError}</p>}
          </div>

          <div>
            <input
              value={zipCode}
              onChange={handleZipChange}
              className={`w-full input px-4 py-2 rounded-xl ${zipCodeError ? "border-red-500" : ""}`}
              placeholder="Irányítószám"
              maxLength={4}
            />
            {zipCodeError && <p className="text-red-500 text-xs">{zipCodeError}</p>}
          </div>

          <div>
            <select
              value={settlement || ""}
              onChange={handleSelectChange(setSettlement)}
              className={`w-full input px-4 py-2 rounded-xl ${settlementError ? "border-red-500" : ""}`}
              disabled
            >
              <option value="" disabled>
                Település
              </option>
              {getCombo("settlement")?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            {settlementError && (
              <p className="text-red-500 text-xs">{settlementError}</p>
            )}
          </div>

          <div>
            <input
              value={roadName}
              onChange={handleInputChange(setRoadName)}
              className={`w-full input px-4 py-2 rounded-xl ${roadNameError ? "border-red-500" : ""}`}
              placeholder="Utca neve"
            />
            {roadNameError && <p className="text-red-500 text-xs">{roadNameError}</p>}
          </div>

          <div>
            <select
              value={typesOfRoad || ""}
              onChange={handleSelectChange(setTypesOfRoad)}
              className={`w-full input px-4 py-2 rounded-xl ${typesOfRoadError ? "border-red-500" : ""}`}
            >
              <option value="" disabled>
                Közterület jellege
              </option>
              {getCombo("typesOfRoad")?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            {typesOfRoadError && (
              <p className="text-red-500 text-xs">{typesOfRoadError}</p>
            )}
          </div>

          <div>
            <input
              value={houseNumber}
              onChange={handleInputChange(setHouseNumber)}
              className={`w-full input px-4 py-2 rounded-xl ${houseNumberError ? "border-red-500" : ""}`}
              placeholder="Házszám"
            />
            {houseNumberError && (
              <p className="text-red-500 text-xs">{houseNumberError}</p>
            )}
          </div>

          <div>
            <input
              value={building}
              onChange={handleInputChange(setBuilding)}
              className={`w-full input px-4 py-2 rounded-xl ${buildingError ? "border-red-500" : ""}`}
              placeholder="Épület"
            />
            {buildingError && <p className="text-red-500 text-xs">{buildingError}</p>}
          </div>

          <div>
            <input
              value={stairway}
              onChange={handleInputChange(setStairway)}
              className={`w-full input px-4 py-2 rounded-xl ${stairwayError ? "border-red-500" : ""}`}
              placeholder="Lépcsőház"
            />
            {stairwayError && (
              <p className="text-red-500 text-xs">{stairwayError}</p>
            )}
          </div>

          <div>
            <input
              value={floor}
              onChange={handleInputChange(setFloor)}
              className={`w-full input px-4 py-2 rounded-xl ${floorError ? "border-red-500" : ""}`}
              placeholder="Emelet"
            />
            {floorError && <p className="text-red-500 text-xs">{floorError}</p>}
          </div>

          <div>
            <input
              value={door}
              onChange={handleInputChange(setDoor)}
              className={`w-full input px-4 py-2 rounded-xl ${doorError ? "border-red-500" : ""}`}
              placeholder="Ajtó"
            />
            {doorError && <p className="text-red-500 text-xs">{doorError}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function AddressForm(props) {
  const [country, setCountry] = useState(props.value?.country || null);
  const [zipCode, setZipCode] = useState(props.value?.zipCode || "");
  const [settlement, setSettlement] = useState(props.value?.settlement || null);
  const [roadName, setRoadName] = useState(props.value?.roadName || "");
  const [typesOfRoad, setTypesOfRoad] = useState(props.value?.typesOfRoad || null);
  const [houseNumber, setHouseNumber] = useState(props.value?.houseNumber || "");
  const [building, setBuilding] = useState(props.value?.building || "");
  const [stairway, setStairway] = useState(props.value?.stairway || "");
  const [floor, setFloor] = useState(props.value?.floor || "");
  const [door, setDoor] = useState(props.value?.door || "");

  const defaultFieldNameMap = {
    country: 'country',
    zipCode: 'postCode',
    settlement: 'settlement',
    roadName: 'roadName',
    typesOfRoad: 'typesOfRoad',
    houseNumber: 'houseNumber',
    building: 'building',
    stairway: 'stairway',
    floor: 'floor',
    door: 'door',
  };

  const tempFieldNameMap = {
    country: 'tempCountry',
    zipCode: 'tempPostCode',
    settlement: 'tempSettlement',
    roadName: 'tempRoadName',
    typesOfRoad: 'tempTypesOfRoad',
    houseNumber: 'tempHouseNumber',
    building: 'tempBuilding',
    stairway: 'tempStairway',
    floor: 'tempFloor',
    door: 'tempDoor',
  };

  useEffect(() => {
      setCountry(props.value?.country || null);
      setZipCode(props.value?.postCode || "");
      setSettlement(props.value?.settlement || null);
      setRoadName(props.value?.roadName || "");
      setTypesOfRoad(props.value?.typesOfRoad || null);
      setHouseNumber(props.value?.houseNumber || "");
      setBuilding(props.value?.building || "");
      setStairway(props.value?.stairway || "");
      setFloor(props.value?.floor || "");
      setDoor(props.value?.door || "");
  }, [props.value]);

  const handleValueChange = (value, fieldNameMap) => {
    const convertedValue = convertFieldName(value, fieldNameMap);
    props.onValueChange?.({ value: convertedValue });
  };

  const convertFieldName = (map, fieldNameMap) => {
    const convertedMap = {};
    for (const key in map) {
      convertedMap[fieldNameMap[key]] = map[key];
    }
    return convertedMap;
  };

  return (
    <FormBlock {...props} title="Lakcím adataim" titleColor={props.color}>
      <div className="form-field-grid items-start">
      {/* Permanent Address */}
      <AddressFormBlock
        color={props.color}
        open={true}
        error={props.error}
        value={props.value}
        onValueChange={(value) => handleValueChange(value, defaultFieldNameMap)}
        title="Állandó lakcímem"
        lead="Lakcím kártyádon szereplő, állandó tartózkodási címed."
        fieldNameMap={defaultFieldNameMap}
      />

      {/* Temporary Address */}
      <AddressFormBlock
        color={props.color}
        error={props.error}
        value={props.value}
        onValueChange={(value) => handleValueChange(value, tempFieldNameMap)}
        title="Ideiglenes lakcímem"
        lead="Csak akkor töltsd ki, ha rendelkezel ideiglenes lakcímmel."
        fieldNameMap={tempFieldNameMap}
      />
      </div>
    </FormBlock>
  );
}

export function OfficeForm(props) {
  const [officeId, setOfficeId] = useState(
    (props.value?.officeList && props.value.officeList[0]) || ""
  );
  const [officeIdError, setOfficeIdError] = useState(null);

  const { getCombo } = useConfig(); 

  useEffect(() => {
    props.onValueChange({ value: { officeList: officeId ? [officeId] : [] } });
  }, [officeId]);

  useEffect(() => {
      setOfficeId((props.value?.officeList && props.value.officeList[0]) || ""); 
  }, [props.value]);

  useEffect(() => {
    if (props.error) {
      const error = getErrorMessage(props.error, "officeList");
      setOfficeIdError(error);
    } else {
      setOfficeIdError(null);
    }
  }, [props.error]);

  const handleChange = (event) => {
    setOfficeId(event.target.value);
  };

  return (
    <FormBlock
      {...props}
      title="Iroda választás"
      lead="Válaszd ki, hogy melyik irodánkba tudsz személyesen is bemenni."
      titleColor={props.color}
    >
      <div>
        <select
          value={officeId || ""}
          onChange={handleChange}
          className={`input px-4 py-2 w-full rounded-xl ${
            officeIdError ? "border-red-500" : ""
          }`}
        >
          <option value="">
            Válassz egy irodát!
          </option>
          {getCombo("office")?.map((office) => (
            <option key={office.id} value={office.id}>
              {office.name}
            </option>
          ))}
        </select>
        {officeIdError && (
          <p className="text-red-500 text-xs mt-1">{officeIdError}</p>
        )}
      </div>
    </FormBlock>
  );
}

export function PasswordForm(props) {
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState(null);

    const [passwordAgain, setPasswordAgain] = useState('');
    const [passwordAgainError, setPasswordAgainError] = useState(null);

    const [passwordStrength, setPasswordStrength] = useState(0);

    useEffect(() => {
        setPassword('');
        setPasswordAgain('');
    }, [props.resetTrigger]);

    useEffect(() => {
        let strength = 0;
        if (password !== '') {
            if (password.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/)) {
                strength = 1;
            } else if (password.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/)) {
                strength = 0.66;
            } else if (password.match(/^(?=.*[a-z])(?=.*[0-9])(?=.{8,})/)) {
                strength = 0.33;
            } else if (password.match(/^(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/)) {
                strength = 0.33;
            } else {
                strength = 0.33;
            }
        }
        setPasswordStrength(strength);
    }, [password]);

    const getFieldErrorMessage = (fieldError) => {
      if (!fieldError) return null;

      if (Array.isArray(fieldError)) {
        const msgs = fieldError
          .map((e) => {
            if (e?.key === "UserModification.EmptyFields" && !e?.message) {
              return "Kötelező megadni!";
            }
            return e?.message ?? null;
          })
          .filter(Boolean);

        return msgs.length > 0 ? msgs.join(" ") : null;
      }

      if (typeof fieldError === "object") {
        if (fieldError.key === "UserModification.EmptyFields" && !fieldError.message) {
          return "Kötelező megadni!";
        }
        return fieldError.message ?? null;
      }

      return String(fieldError);
    };

    useEffect(() => {
        if (password !== passwordAgain) {
            setPasswordAgainError('A jelszavak nem egyeznek');
        } else {
          setPasswordAgainError(null);
        }
    }, [password, passwordAgain]);

    useEffect(() => {
      props.onValueChange({ value: { password, passwordAgain } });
    }, [password, passwordAgain]); 

    const getColorByPasswordStrength = () => {
        if (passwordStrength <= 0.33) return 'red';
        if (passwordStrength <= 0.66) return 'yellow';
        return 'green';
    };

    useEffect(() => {
      if (!props.error) {
        setPasswordError(null);
        return;
      }

      const backendPasswordErr = getFieldErrorMessage(props.error.password);
      const backendPasswordAgainErr = getFieldErrorMessage(props.error.passwordAgain);

      setPasswordError(backendPasswordErr || null);

      if (backendPasswordAgainErr) {
        setPasswordAgainError(backendPasswordAgainErr);
      }
    }, [props.error]);

    useEffect(() => {
        setPasswordError(null);
    }, [password]);

    useEffect(() => {
        setPasswordAgainError(null);
    }, [passwordAgain]);

    return (
        <FormBlock
            {...props}
            title="Jelszavam"
            lead={`Az itt megadott jelszóval tudsz majd belépni az oldalunkra és az applikációba.${
                props.edit ? '' : ' A megadott jelszót bármikor módosíthatod a belépést követően.'
            }`}
            titleColor={props.color}
        >
          <div className='form-field-grid'>
            <div className="form-field">
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`input w-full min-w-0 rounded-xl px-4 py-2 ${
                        passwordError ? 'border-red-500' : ''
                    }`}
                    placeholder={props.edit ? 'Új jelszavam' : 'Jelszavam'}
                />
                {passwordError && <p className="text-red-500 text-xs">{passwordError}</p>}
            </div>

            <div className="form-field">
                <input
                    type="password"
                    value={passwordAgain}
                    onChange={(e) => setPasswordAgain(e.target.value)}
                    className={`input w-full min-w-0 rounded-xl px-4 py-2 ${
                        passwordAgainError ? 'border-red-500' : ''
                    }`}
                    placeholder={props.edit ? 'Új jelszavam újra' : 'Jelszavam újra'}
                />
                {passwordAgainError && <p className="text-red-500 text-xs">{passwordAgainError}</p>}
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
              <p className={`shrink-0 font-semibold ${props.variant === 'registration' ? 'text-sm text-neutral-700' : 'text-base text-white'}`}>
                Jelszavad erőssége
              </p>

              <div className="relative h-2 w-full max-w-lg overflow-hidden rounded-full bg-gray-200">
                  <motion.div
                      className={`absolute h-full w-full rounded-full gradient-${getColorByPasswordStrength()}`} 
                      initial={{ width: '0%' }}
                      animate={{ width: `${passwordStrength * 100}%` }}
                      transition={{ duration: 0.5 }}
                  ></motion.div>
              </div>
          </div>
        </FormBlock>
    );
}

export function PrivacyForm(props) {
  const [privacyStatementAccepted, setPrivacyStatementAccepted] = useState(props.edit ? true : false);
  const [privacyStatementAcceptedError, setPrivacyStatementAcceptedError] = useState(null);

  const [subscribeNewsletter, setSubscribeNewsletter] = useState(
      props.value && props.value.subscribeNewsletter || false
  );
  const [subscribeNewsletterError, setSubscribeNewsletterError] = useState(null);

  useEffect(() => {
      props.onValueChange?.({
          value: { privacyStatementAccepted, subscribeNewsletter },
      });
  }, [privacyStatementAccepted, subscribeNewsletter]);

  useEffect(() => {
    setPrivacyStatementAccepted(props.edit ? true : false);
    setSubscribeNewsletter(props.value && props.value.subscribeNewsletter || false); 
  }, [props.value]);

  useEffect(() => {
      if (props.error?.privacyStatementAccepted) {
          setPrivacyStatementAcceptedError(props.error.privacyStatementAccepted);
      }
      if (props.error?.subscribeNewsletter) {
          setSubscribeNewsletterError(props.error.subscribeNewsletter);
      }
  }, [props.error]);

  

  useEffect(() => {
      setPrivacyStatementAcceptedError(null);
  }, [privacyStatementAccepted]);

  useEffect(() => {
      setSubscribeNewsletterError(null);
  }, [subscribeNewsletter]);

  return (
      <FormBlock
          {...props}
          title={!props.edit ? 'Adataim védelme' : 'Hírlevél feliratkozás'}
          lead={
              !props.edit
                  ? 'Fogadd el az Adatkezelési Tájékoztató Diákoknak dokumentumot, hogy felvehessük veled a kapcsolatot álláslehetőségeinkkel kapcsolatban, és elektronikus értesítéseket küldhessünk neked.'
                  : null
          }
          titleColor={props.color}
      >
        {!props.edit && (
            <Link
                className={`${FORM_ACTION_SECONDARY} w-full sm:w-auto`}
                to="/adatkezelési-tájékoztató-diákoknak"
                target="_blank"
            >
                Adatkezelési Tájékoztató Diákoknak
            </Link>
        )}

        {!props.edit && (
          <div>
              <label className="flex items-start gap-3 cursor-pointer">
                  <input
                      type="checkbox"
                      checked={privacyStatementAccepted}
                      onChange={(e) => setPrivacyStatementAccepted(e.target.checked)}
                      className={`hidden form-checkbox h-5 w-5 ${
                          privacyStatementAcceptedError ? 'border-red-500' : ''
                      }`}
                  />
                  <CheckboxMark
                    checked={privacyStatementAccepted}
                    error={privacyStatementAcceptedError}
                    variant={props.variant}
                    borderColor={props.borderColor}
                    backgroundColor={props.backgroundColor}
                  />
                  <span className={props.variant === 'registration' ? 'text-sm text-neutral-700' : 'text-white'}>
                      Elfogadom az Adatkezelési Tájékoztató Diákoknak dokumentumot
                  </span>
              </label>
              {privacyStatementAcceptedError && (
                  <p className="text-red-500 text-xs">{privacyStatementAcceptedError}</p>
              )}
          </div>
        )}

        <p className={props.variant === 'registration' ? 'text-neutral-600 text-sm' : 'text-white text-xs'}>
            Iratkozz fel hírlevelünkre, hogy azonnal értesíteni tudjunk legújabb híreinkről, nyereményjátékainkról és aktualitás akcióinkról.
        </p>

        <div>
          <label className="flex items-start gap-3 cursor-pointer">
              <input
                  type="checkbox"
                  checked={subscribeNewsletter}
                  onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                  className="hidden"
              />
              <CheckboxMark
                checked={subscribeNewsletter}
                variant={props.variant}
                borderColor={props.borderColor}
                backgroundColor={props.backgroundColor}
              />
              <span className={props.variant === 'registration' ? 'text-sm text-neutral-700' : 'text-white'}>
                Feliratkozom a hírlevélre
              </span>
          </label>

          {subscribeNewsletterError && (
              <p className="text-red-500 text-xs">{subscribeNewsletterError}</p>
          )}
        </div>

        {!props.edit && (
            <p className={props.variant === 'registration' ? 'text-neutral-500 text-xs' : 'text-white text-xs'}>
                A hírlevélről bármikor leiratkozhatsz, akár a saját fiókodba történő belépés után is.
            </p>
        )}
      </FormBlock>
  );
}

export function ProfilePictureForm(props) {
  const { userData } = useAuth();
  const fileInputRef = useRef(null);
  const [removeCurrentPhoto, setRemoveCurrentPhoto] = useState(false);
  const currentPhotoKey = removeCurrentPhoto ? null : props.value?.photoKey ?? userData?.photoKey;

  const image = useMemo(() => {
    return currentPhotoKey
      ? { uri: `${global.backingsEndpoint}photoRepository/getImageBySize?size=ORIGINAL&key=${currentPhotoKey}` }
      : null;
  }, [currentPhotoKey]);

  const [newImage, setNewImage] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState(null);

  useEffect(() => {
      props.onValueChange?.({
          value: {
              file: newImage?.file ?? null,
              removePhoto: removeCurrentPhoto,
          },
      });
  }, [newImage, removeCurrentPhoto]);

  useEffect(() => {
    return () => {
      if (newImage?.uri) URL.revokeObjectURL(newImage.uri);
    };
  }, [newImage]);

  const getPicture = () => fileInputRef.current?.click();

const handleFileUpload = (file) => {
  if (!file) return;

  if (!file.type?.startsWith("image/")) {
      setFileError("Kérjük, képfájlt válassz!");
      return;
  }

  if (file.size > 2 * 1024 * 1024) {
      setFileError("A fájl mérete nem haladhatja meg a 2 MB-ot. Válassz kisebb képet!");
      return;
  }

  const objectUrl = URL.createObjectURL(file);
  setFileError(null);
  setRemoveCurrentPhoto(false);
  setNewImage({ uri: objectUrl, file, name: file.name });
};

const handleFileInputChange = (event) => {
  handleFileUpload(event.target.files?.[0]);
  event.target.value = "";
};

// Drag Over Event
const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
};

// Drag Leave Event
const handleDragLeave = () => {
    setDragging(false);
};

// Drop Event (Handles file drop)
const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) {
        handleFileUpload(file);
    }
};

  const removeNewImage = () => {
    setNewImage(null);
    setFileError(null);
  };

  const previewImageUri = newImage?.uri || image?.uri || "/assets/images/avatar.png";
  const previewLabel = newImage
    ? "Új profilkép előnézete"
    : currentPhotoKey
      ? "Jelenlegi profilkép"
      : "Alapértelmezett profilkép";

  return (
      <FormBlock
          {...props}
          title="Profilképem"
          lead="A profilképed négyzetes (1:1) kivágásban jelenik meg. Válassz jól felismerhető, legfeljebb 2 MB méretű képet."
          titleColor={props.color}
      >
          <div className="w-full rounded-3xl border border-green-100 bg-gradient-to-br from-slate-50 to-green-50/70 p-4 shadow-inner sm:p-5 lg:p-6">
              <input
                ref={fileInputRef}
                className="sr-only"
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
              />

              <div className="grid min-w-0 grid-cols-1 items-center gap-5 md:grid-cols-[13rem_minmax(0,1fr)] lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-7">
                <div className="flex min-w-0 flex-col items-center gap-3">
                  <div className={`relative aspect-square w-44 overflow-hidden rounded-2xl bg-white shadow-md ring-4 sm:w-48 md:w-full ${
                    newImage ? "ring-green-200" : "ring-white"
                  }`}>
                    <img
                      className="size-full object-cover"
                      src={previewImageUri}
                      alt={previewLabel}
                    />
                    {newImage && (
                      <span className="absolute bottom-2 left-2 rounded-full bg-green-700/90 px-3 py-1 text-xs font-bold text-white shadow-sm">
                        Új kép
                      </span>
                    )}
                  </div>

                  <p className="text-center text-sm font-bold text-neutral-600">{previewLabel}</p>

                  {!newImage && !removeCurrentPhoto && currentPhotoKey && (
                    <button
                      type="button"
                      onClick={() => {
                        setRemoveCurrentPhoto(true);
                        setFileError(null);
                      }}
                      className={`${FORM_ACTION_DANGER} w-full sm:w-auto`}
                    >
                      <PiTrash className="size-5 shrink-0" />
                      Profilkép törlése
                    </button>
                  )}
                </div>

                <div
                  className={`${FORM_UPLOAD_DROPZONE} ${getFormUploadDropzoneState(dragging)}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <span className={`flex size-12 shrink-0 items-center justify-center rounded-xl ${getFormUploadIconState(dragging)}`}>
                    <FaFileUpload className="text-2xl" />
                  </span>

                  <div className="min-w-0">
                    <h3 className="text-lg font-black leading-tight text-neutral-800">
                      {newImage ? "A kép készen áll a mentésre" : "Tölts fel új profilképet"}
                    </h3>
                    <p className={`mt-1 text-sm leading-relaxed ${newImage ? "break-all font-bold text-green-700" : "text-neutral-600"}`}>
                      {newImage?.name || "Húzd ide a képet, vagy válaszd ki a készülékedről."}
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">Képfájl, legfeljebb 2 MB</p>
                  </div>

                  {fileError && (
                    <p className="w-full rounded-xl bg-red-50 px-3 py-2 text-sm font-bold text-red-600" role="alert">
                      {fileError}
                    </p>
                  )}

                  <div className={FORM_UPLOAD_ACTIONS}>
                    <button
                      className={`${FORM_ACTION_PRIMARY} w-full sm:w-auto`}
                      onClick={getPicture}
                      type="button"
                    >
                      <PiUploadSimple className="size-5 shrink-0" />
                      {newImage ? "Másik kép választása" : "Kép kiválasztása"}
                    </button>

                    {newImage && (
                      <button
                        className={`${FORM_ACTION_DANGER} w-full sm:w-auto`}
                        onClick={removeNewImage}
                        type="button"
                      >
                        <PiTrash className="size-5 shrink-0" />
                        Kiválasztás törlése
                      </button>
                    )}
                  </div>
                </div>
              </div>
          </div>
      </FormBlock>
  );
}

export function CVForm(props) {
  const [cvUploaded, setCvUploaded] = useState(props.value?.cvUploaded || false);
  const [cvExpirationDate, setCvExpirationDate] = useState(props.value?.cvExpirationDate || null);
  const [cvFileKey, setCvFileKey] = useState(props.value?.cvFileKey || null);
  const [cvFileKeyError, setCvFileKeyError] = useState(null);
  const [cvAcceptTerms, setCvAcceptTerms] = useState(props.value?.cvAcceptTerms || false);
  const [cvAcceptTermsError, setCvAcceptTermsError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragging, setDragging] = useState(false);

  const hasCv = !!cvExpirationDate || !!cvFileKey;

  useEffect(() => {
      let value = { cvAcceptTerms, cvUploaded: hasCv }
      if (props.edit)
          value.cvFile = cvFileKey;
      else
          value.cvFileKey = cvFileKey;

      props.onValueChange({ value: value });
  }, [cvFileKey, cvAcceptTerms, hasCv/*, cvUploaded*/]);

  useEffect(() => {
    setCvUploaded(props.value?.cvUploaded ?? false);
    setCvExpirationDate(props.value?.cvExpirationDate ?? null);
    setCvFileKey(props.value?.cvFileKey ?? props.value?.cvFile ?? null);
    setCvAcceptTerms(props.value?.cvAcceptTerms ?? false);
  }, [props.value]);

  useEffect(() => {
    setCvFileKeyError(getErrorMessage(props.error, "cvFileKey"));
    setCvAcceptTermsError(getErrorMessage(props.error, "cvAcceptTerms"));
  }, [props.error]);

  const downloadCv = () => {
    if (!cvFileKey) return;

    const url = `${global.backingsEndpoint}cvControl/getFile?key=${encodeURIComponent(cvFileKey)}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const deleteCv = () => {
    setCvUploaded(false);
    setCvAcceptTerms(false);
    setCvExpirationDate(null);
    setCvFileKey(null);
    setSelectedFile(null);
  };

  async function uploadCvFile(file) {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      global.backingsEndpoint + "cvControl/addFile",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    if (!res.data?.successful) {
      throw new Error("Upload failed");
    }

    return res.data;
  }

  const handleFileUpload = async (event) => {
    const file = event.target?.files?.[0] || event.dataTransfer?.files?.[0];
    if (!file) return;

    try {
      setCvFileKeyError(null);

      const result = await uploadCvFile(file);

      setCvFileKey(result.key);
      setSelectedFile(file);
      setCvUploaded(true);
    } catch (e) {
      setCvFileKey(null);
      setCvUploaded(false);
      setCvFileKeyError("Sikertelen feltöltés, kérlek próbáld újra.");
      console.error(e);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    handleFileUpload(event);
    setDragging(false);
  };

  return (
    <FormBlock
      {...props}
      title="Önéletrajzom"
      lead="Az önéletrajz feltöltése meggyorsítja a jelentkezést azokra a munkáinkra, ahol az önéletrajz a jelentkezés feltétele."
      titleColor={props.color}
    >
      <div className='grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-2 lg:items-start'>
        <div className='min-w-0 w-full'>
          {/* Drag and Drop Upload Box */}
          <div
            className={`${FORM_UPLOAD_DROPZONE} ${getFormUploadDropzoneState(dragging)}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <span className={`flex size-12 shrink-0 items-center justify-center rounded-xl ${getFormUploadIconState(dragging)}`}>
              {hasCv ? <PiCheckBold className="text-xl" /> : <FaFileUpload className="text-2xl" />}
            </span>

            {hasCv ? (
              <>
                <div className="min-w-0">
                  <h3 className="text-lg font-black leading-tight text-neutral-800">Az önéletrajzod feltöltve</h3>
                  {selectedFile?.name && (
                    <p className="mt-1 break-all text-sm font-bold text-green-700">{selectedFile.name}</p>
                  )}
                  <p className="mt-1 text-sm leading-relaxed text-neutral-600">
                    Automatikus törlés: {cvExpirationDate ? new Date(cvExpirationDate).toLocaleString("hu-HU") : "—"}
                  </p>
                  <p className="mt-1 text-xs text-neutral-500">A fájlt letöltheted vagy eltávolíthatod.</p>
                </div>

                {cvFileKeyError && (
                  <p className="w-full rounded-xl bg-red-50 px-3 py-2 text-sm font-bold text-red-600" role="alert">
                    {cvFileKeyError}
                  </p>
                )}

                <div className={FORM_UPLOAD_ACTIONS}>
                  <button
                    type="button"
                    className={`${FORM_ACTION_SECONDARY} w-full sm:w-auto`}
                    onClick={downloadCv}
                    disabled={!cvFileKey}
                  >
                    <PiDownloadSimple className="size-5" />
                    Letöltés
                  </button>

                  <button
                    type="button"
                    className={`${FORM_ACTION_DANGER} w-full sm:w-auto`}
                    onClick={deleteCv}
                  >
                    <PiTrash className="size-5" />
                    Törlés
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="min-w-0">
                  <h3 className="text-lg font-black leading-tight text-neutral-800">Töltsd fel az önéletrajzodat</h3>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-600">
                    Húzd ide a fájlt, vagy válaszd ki a készülékedről.
                  </p>
                  <p className="mt-1 text-xs text-neutral-500">PDF, DOC vagy DOCX, legfeljebb 2 MB</p>
                </div>

                {cvFileKeyError && (
                  <p className="w-full rounded-xl bg-red-50 px-3 py-2 text-sm font-bold text-red-600" role="alert">
                    {cvFileKeyError}
                  </p>
                )}

                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  id="cvFileUpload"
                  onChange={handleFileUpload}
                />
                <div className={FORM_UPLOAD_ACTIONS}>
                  <label
                    htmlFor="cvFileUpload"
                    className={`${FORM_ACTION_PRIMARY} w-full cursor-pointer sm:w-auto`}
                  >
                    <PiUploadSimple className="size-5 shrink-0" />
                    Fájl kiválasztása
                  </label>
                </div>
              </>
            )}
          </div>
        </div>

        <div className='flex min-w-0 w-full flex-col justify-center gap-4'>
          {/* Terms Agreement */}
          <p className={`text-sm leading-relaxed ${props.variant === 'registration' ? 'text-neutral-600' : 'text-white'}`}>
            Hozzájárulok, hogy csatolt önéletrajzomat és az abban szereplő személyes adataimat a Fürge Diák Iskolaszövetkezet az Adatkezelési tájékoztatójában szereplők szerint a feltöltés időpontjától számított 365 napig kezelje.
            <br/>
            <br/>
            Hozzájárulok továbbá ahhoz, hogy az általam megküldött önéletrajzot az Iskolaszövetkezet a Szolgáltatás fogadója részére tovább küldje, és az a munkavégzéshez kapcsolódóan az adataimat kezelje.
            <br/>
            <br/>
            Tudomásul veszem, hogy feltöltött önéletrajzom törlését bármikor kezdeményezhetem a www.furgediak.hu oldalra belépést követően felhasználói fiókom Önéletrajzom menüpontjában.
          </p>

          <label className={`flex items-center gap-3 ${hasCv ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
              <input
                type="checkbox"
                className="sr-only"
                checked={cvAcceptTerms}
                onChange={(e) => setCvAcceptTerms(e.target.checked)}
                disabled={!hasCv}
              />
              <CheckboxMark
                checked={cvAcceptTerms}
                error={cvAcceptTermsError}
                disabled={!hasCv}
                variant={props.variant}
                borderColor={props.borderColor}
                backgroundColor={props.backgroundColor}
              />
              <span className={props.variant === 'registration' ? 'text-sm font-medium text-neutral-700' : 'text-lg text-white'}>
                Elfogadom a feltételeket
              </span>
          </label>

          {cvAcceptTermsError && <p className="text-red-500 text-xs mt-2">{cvAcceptTermsError}</p>}
        </div>
      </div>
    </FormBlock>
  );
}

// Still having problem with deleting the subforms after add many
export function LanguageForm(props) {
  const mapFromProps = (value) =>
    (value?.languageSkills ?? []).map((s) => ({
      id: s.id,
      languageId: s.language,
      skillId: s.skill,
      _tmpId: `${s.id ?? crypto.randomUUID()}`,
    }));

  const [languageSkills, setLanguageSkills] = useState(() =>
    mapFromProps(props.value)
  );
  const [deletedLanguageIds, setDeletedLanguageIds] = useState([]);
  const hasLanguageSkill = languageSkills.length > 0;

  useEffect(() => {
    setLanguageSkills(mapFromProps(props.value));
    setDeletedLanguageIds([]);
  }, [props.value]);

  useEffect(() => {
    const kept = (languageSkills || []).map(({ _tmpId, ...rest }) => rest);

    const deletedMarkers = (deletedLanguageIds || []).map((id) => ({ id }));

    props.onValueChange?.({
      config: { dto: "lsDto" },
      value: {
        languageSkills: [...kept, ...deletedMarkers],
      },
    });
  }, [languageSkills, deletedLanguageIds]);

  const handleYesClick = () => {
    setLanguageSkills(prev => prev.length ? prev : [
          {
            id: null,
            languageId: null,
            skillId: null,
            _tmpId: crypto.randomUUID(),
          },
        ]
    );
  };

  const handleNoClick = () => {
    const persistedIds = languageSkills
      .map((item) => item.id)
      .filter((id) => id != null);

    if (persistedIds.length > 0) {
      setDeletedLanguageIds((prev) => [
        ...prev,
        ...persistedIds.filter((id) => !prev.includes(id)),
      ]);
    }

    setLanguageSkills([]);
  };

  const addNew = () => {
    setLanguageSkills(prev => [
      ...prev,
      {
        languageId: null,
        skillId: null,
        _tmpId: crypto.randomUUID(),
      },
    ]);
  };

  const remove = (el) => {
    const key = el.id ?? el._tmpId;

    if (el.id != null) {
      setDeletedLanguageIds((prev) =>
        prev.includes(el.id) ? prev : [...prev, el.id]
      );
    }

    setLanguageSkills((prev) => {
      return prev.filter((x) => (x.id ?? x._tmpId) !== key);
    });
  };

  const handleSubFormValueChange = (value) => {
    const key = value.id ?? value._tmpId;
    setLanguageSkills(prev => prev.map(x => ((x.id ?? x._tmpId) === key ? value : x)));
  };

  return (
      <FormBlock {...props} title="Nyelvtudásaim" lead="Idegen nyelvet beszélek" titleColor={props.color}>
        {/* No Language Skill Selection */}
        <div className="flex flex-col gap-2">
            <label className={`block font-black ${props.color}`}>Beszélsz idegen nyelvet?</label>
            <div className="grid w-full max-w-sm grid-cols-2 gap-3">
                <FormChoice
                    name="hasLanguageSkill"
                    value="true"
                    selected={hasLanguageSkill}
                    onChange={handleYesClick}
                    registrationVariant={props.variant === 'registration'}
                >
                    Igen
                </FormChoice>
                <FormChoice
                    name="hasLanguageSkill"
                    value="false"
                    selected={!hasLanguageSkill}
                    onChange={handleNoClick}
                    registrationVariant={props.variant === 'registration'}
                >
                    Nem
                </FormChoice>
            </div>
        </div>

        {hasLanguageSkill && (
            <div className="flex flex-col gap-4">
                {languageSkills.map((item, index) => (
                    <LanguageSubForm
                        color={props.color}
                        variant={props.variant}
                        key={item.id ?? item._tmpId}
                        className={index === languageSkills.length - 1 ? "" : "mb-3"}
                        value={item}
                        onValueChange={handleSubFormValueChange}
                        onRemove={remove}
                    />
                ))}

                {languageSkills.length <= 3 && (
                    <button
                        type="button"
                        className={`${FORM_ACTION_PRIMARY} w-full sm:w-auto`}
                        onClick={addNew}
                    >
                        Nyelvtudást adok hozzá (+)
                    </button>
                )}
            </div>
        )}
      </FormBlock>
  );
}

function LanguageSubForm(props) {
  const { getCombo } = useConfig();
  const [languageId, setLanguageId] = useState(props.value.languageId ?? null);
  const [skillId, setSkillId] = useState(props.value.skillId ?? null);

  useEffect(() => {
    setLanguageId(props.value.languageId ?? null);
    setSkillId(props.value.skillId ?? null);
  }, [props.value.id, props.value._tmpId, props.value.languageId, props.value.skillId]);

  useEffect(() => {
      props.onValueChange({ ...props.value, languageId, skillId });
  }, [languageId, skillId]);

  return (
      <FormBlock
          {...props}
          title="Nyelv"
          lead="Add meg a nyelvet és a tudásszintet."
          titleColor={props.color}
      >
        <div className="form-field-grid items-end">
          <div className="form-field">
              <label className={`block font-black ${props.color}`}>Nyelv</label>
              <select
                  className="input w-full px-4 py-2 rounded-xl"
                  value={languageId || ""}
                  onChange={(e) => setLanguageId(e.target.value)}
              >
                  <option value="" disabled>
                      Válassz nyelvet
                  </option>
                  {getCombo("language")?.map((language) => (
                    <option key={language.id} value={language.id}>
                      {language.name}
                    </option>
                  ))}
              </select>
          </div>

          {/* Skill Level Picker */}
          <div className="form-field">
              <label className={`block font-black ${props.color}`}>Tudásszintem</label>
              <select
                  className="input w-full px-4 py-2 rounded-xl"
                  value={skillId || ""}
                  onChange={(e) => setSkillId(e.target.value)}
              >
                  <option value="" disabled>
                      Válassz szintet
                  </option>
                  {getCombo("skill")?.map((skill) => (
                    <option key={skill.id} value={skill.id}>
                      {skill.name}
                    </option>
                  ))}
              </select>
          </div>

          <button
              type='button'
              className={`${FORM_ACTION_DANGER} w-full lg:col-span-2 lg:w-auto lg:justify-self-end`}
              onClick={() => props.onRemove(props.value)}
          >
              Nyelv törlése (-)
          </button>
        </div>
      </FormBlock>
  );
}

export function DrivingLicenseForm(props) {
    const [drivingLicence, setDrivingLicence] = useState(props.value?.drivingLicence || false);
    const [drivingLicenceError, setDrivingLicenceError] = useState(null);

    const [drivingLicenceTypeList, setDrivingLicenceTypeList] = useState(props.value?.drivingLicenceTypeList || []);
    const [drivingLicenceTypeListError, setDrivingLicenceTypeListError] = useState(null);

    const [hasCar, setHasCar] = useState(props.value?.hasCar || false);
    const [hasCarError, setHasCarError] = useState(null);

    useEffect(() => {
        props.onValueChange({ config: { dto: 'dlDto' }, value: { drivingLicence, hasCar, drivingLicenceTypeList } });
    }, [drivingLicence, hasCar, drivingLicenceTypeList]);

    useEffect(() => {
        setDrivingLicence(props.value?.drivingLicence || false);
        setDrivingLicenceTypeList(props.value?.drivingLicenceTypeList || []);
        setHasCar(props.value?.hasCar || false);
    }, [props.value]);
  
    useEffect(() => {
        if (getErrorMessage(props.error, 'drivingLicence')) setDrivingLicenceError(getErrorMessage(props.error, 'drivingLicence'));
        if (getErrorMessage(props.error, 'drivingLicenceTypeList')) setDrivingLicenceTypeListError(getErrorMessage(props.error, 'drivingLicenceTypeList'));
        if (getErrorMessage(props.error, 'hasCar')) setHasCarError(getErrorMessage(props.error, 'hasCar'));
    }, [props.error]);

    const dlFromString = (dl) => Array.isArray(dl) ? dl : dl ? dl.split(",").map(item => item.trim()) : [];
    const dlToString = (dl) => Array.isArray(dl) && dl.length > 0 ? dl.join(", ") : "";

    useEffect(() => { setDrivingLicenceError(null) }, [drivingLicence]);
    useEffect(() => { setDrivingLicenceTypeListError(null) }, [drivingLicenceTypeList]);
    useEffect(() => { setHasCarError(null) }, [hasCar]);

    useEffect(() => {
      const value = { drivingLicence };

      if (!drivingLicence) {
        value.hasCar = false;
      } else {
        value.hasCar = hasCar;
        value.drivingLicenceTypeList = drivingLicenceTypeList;
      }

      props.onValueChange({
        config: { dto: "dlDto" },
        value,
      });
    }, [drivingLicence, hasCar, drivingLicenceTypeList]);

    const handleDrivingLicenceYes = () => {
      setDrivingLicence(true);
    };

    const handleDrivingLicenceNo = () => {
      setDrivingLicence(false);
      setDrivingLicenceTypeList([]);
      setHasCar(false);
    };

    const handleHasCarYes = () => setHasCar(true);
    const handleHasCarNo = () => setHasCar(false);

    return (
        <FormBlock
            {...props}
            title="Jogosítványom"
            lead="Rendelkezel jogosítvánnyal?"
            titleColor={props.color}
        >
          {/* Driving License */}
          <div className="form-field w-full sm:max-w-sm">
              <label className={`block font-black ${props.color}`}>Rendelkezel jogosítvánnyal?</label>
              <div className="grid grid-cols-2 gap-3">
                  <FormChoice
                      name="drivingLicence"
                      value="true"
                      selected={drivingLicence === true}
                      onChange={handleDrivingLicenceYes}
                      registrationVariant={props.variant === 'registration'}
                  >
                      Igen
                  </FormChoice>
                  <FormChoice
                      name="drivingLicence"
                      value="false"
                      selected={drivingLicence === false}
                      onChange={handleDrivingLicenceNo}
                      registrationVariant={props.variant === 'registration'}
                  >
                      Nem
                  </FormChoice>
              </div>
          </div>

          {/* Driving License Type & Car Ownership */}
          {drivingLicence && (
              <div className="form-field-grid">
                  <div className="form-field">
                      <label className={`block font-black ${props.color}`}>Jogosítványom típusa</label>
                      <p className="text-sm text-white">
                          Kérjük írd be, milyen jogosítvánnyal rendelkezel. Amennyiben több típussal is rendelkezel, úgy veszzővel elválasztva add meg őket. Személygépkocsit B kategóriás jogsival vezethetsz.
                      </p>
                      <input
                          type="text"
                          value={dlToString(drivingLicenceTypeList)}
                          onChange={(e) => setDrivingLicenceTypeList(dlFromString(e.target.value))}
                          className="w-full px-4 py-2 rounded-lg bg-white/10 border border-gray-300 text-white"
                      />
                  </div>

                  <div className="form-field">
                      <label className={`block font-black ${props.color}`}>Rendelkezel gépjárművel?</label>
                      <p className="text-sm text-white">
                          Kérjük add meg, hogy rendelkezel-e a jogosítványodnak megfelelő típusú, saját használatú járművel.
                      </p>
                      <div className="grid w-full max-w-sm grid-cols-2 gap-3">
                          <FormChoice
                              name="hasCar"
                              value="true"
                              selected={hasCar === true}
                              onChange={handleHasCarYes}
                              registrationVariant={props.variant === 'registration'}
                          >
                              Igen
                          </FormChoice>
                          <FormChoice
                              name="hasCar"
                              value="false"
                              selected={hasCar === false}
                              onChange={handleHasCarNo}
                              registrationVariant={props.variant === 'registration'}
                          >
                              Nem
                          </FormChoice>
                      </div>
                  </div>
              </div>
          )}
        </FormBlock>
    );
}

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

const registrationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 54,
    borderRadius: 8,
    borderColor: state.isFocused ? '#86efac' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 4px #dcfce7' : 'none',
    outline: 'none',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
    '&:hover': {
      borderColor: state.isFocused ? '#86efac' : '#9ca3af',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '8px 18px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    boxShadow: 'none',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9ca3af',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#dcfce7',
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#166534',
    fontWeight: 600,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#15803d',
    ':hover': {
      backgroundColor: '#bbf7d0',
      color: '#14532d',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#16a34a' : state.isFocused ? '#f0fdf4' : 'white',
    color: state.isSelected ? 'white' : '#262626',
  }),
};

export function PlaceForm(props) {
  const [countyId, setCountyId] = useState(props.value?.workCountyList || []);
  const [countyIdError, setCountyIdError] = useState(null);
  const prevCountyId = usePrevious(countyId);

  
  const [settlementId, setSettlementId] = useState(props.value?.workSettlementList || []);
  const [settlementIdError, setSettlementIdError] = useState(null);

  const [filteredSettlementList, setFilteredSettlementList] = useState([]);

  const { getCombo } = useConfig();

  const countyList = useMemo(() => {
    const countiesRaw = getCombo("county") || [];
    return countiesRaw.map(c => ({
      value: c.id,
      label: c.name,
      countyID: c.countyID,
    }));
  }, [getCombo]);

  const settlementList = useMemo(() => {
    const settlementsRaw = getCombo("settlement") || [];
    return settlementsRaw
      .filter((s) => s.primary === true)
      .map((s) => ({
        value: s.id,
        label: s.name,
        countyID: s.countyID,
      }));
  }, [getCombo]);

  useEffect(() => {
    const selectedCountyIDs = countyList
      .filter((c) => countyId.includes(c.value))
      .map((c) => c.countyID);

    const filtered = settlementList.filter((s) =>
      selectedCountyIDs.includes(s.countyID)
    );

    setFilteredSettlementList(filtered);

    setSettlementId((prev) => prev.filter((sid) => filtered.some((s) => s.value === sid)));
  }, [countyId, countyList, settlementList]);

  useEffect(() => {
    // Notify parent component of changes
    props.onValueChange({
      value: { workCountyList: countyId, workSettlementList: settlementId },
    });
  }, [countyId, settlementId]);

  useEffect(() => {
      setCountyId(props.value?.workCountyList || []);
      setSettlementId(props.value?.workSettlementList || []);
  }, [props.value]);

  return (
    <FormBlock
      {...props}
      title="Melyik megyében szeretnél dolgozni?"
      lead="Akár többet is megadhatsz."
      titleColor={props.color}
    >
      <div className="form-field-grid">
      {/* County Search Box */}
      <div className="form-field">
        <label className={`block font-black ${props.color}`}>Válassz megyét!</label>
        <Select
          isMulti
          placeholder="Keresés megyék között..."
          options={countyList}
          onChange={(selectedOptions) => {
            const selectedIds = selectedOptions.map((option) => option.value);
            setCountyId(selectedIds);
          }}
          value={countyList.filter((option) => countyId.includes(option.value))}
          className="text-black"
          styles={props.variant === 'registration' ? registrationSelectStyles : undefined}
        />
      </div>

      {/* Settlement Search Box */}
      <div className="form-field">
        <label className={`block font-black ${props.color}`}>Válassz települést!</label>
        <Select
          isMulti
          placeholder="Keresés települések között..."
          options={filteredSettlementList}
          onChange={(selectedOptions) => {
            const selectedIds = (selectedOptions ?? []).map((option) => option.value);
            setSettlementId(selectedIds);
          }}
          value={filteredSettlementList.filter((option) => settlementId.includes(option.value))}
          className="text-black"
          styles={props.variant === 'registration' ? registrationSelectStyles : undefined}
        />
      </div>
      </div>
    </FormBlock>
  );
}

const predefinedJobAreas = [
  { id: 1, name: "Gyorséttermi", color: "blue", icon: "/assets/images/icons/Webicon125.svg", border: "border-sky-400" },	
  { id: 2, name: "Kereskedelmi", color: "blue", icon: "/assets/images/icons/Webicon126.svg", border: "border-sky-400" },	
  { id: 3, name: "Könnyű fizikai", color: "green", icon: "/assets/images/icons/Webicon127.svg", border: "border-lime-400" },
  { id: 4, name: "Mezőgazdasági", color: "yellow", icon: "/assets/images/icons/Webicon128.svg", border: "border-orange-400" },	
  { id: 5, name: "Szellemi", color: "green", icon: "/assets/images/icons/Webicon129.svg", border: "border-lime-400" },
  { id: 6, name: "Üzemi", color: "green", icon: "/assets/images/icons/Webicon130.svg", border: "border-lime-400" },
  { id: 7, name: "Vendéglátás / Idegenforgalom", color: "yellow", icon: "/assets/images/icons/Webicon131.svg", border: "border-orange-400" },
];

export function JobAreaForm(props) {
  const [jobAreaList, setJobAreaList] = useState(props.value?.workJobAreaList || []);
  const { getCombo } = useConfig();

  const jobAreas = getCombo("jobArea").map((jobArea) => ({
    ...predefinedJobAreas.find((predefined) => predefined.id === jobArea.id),
    ...jobArea,
  }));

  useEffect(() => {
    props.onValueChange({ value: { workJobAreaList: jobAreaList } });
  }, [jobAreaList]);

  useEffect(() => {
      setJobAreaList(props.value?.workJobAreaList || []);
  }, [props.value]);

  const handleToggleJobArea = (id) => {
    setJobAreaList((prev) =>
      prev.includes(id) ? prev.filter((jobAreaId) => jobAreaId !== id) : [...prev, id]
    );
  };

  return (
    <FormBlock
      {...props}
      title="Melyik munkakörben szeretnél dolgozni?"
      lead="Akár többet is megadhatsz."
      titleColor={props.color}
    >
      <div className="grid w-full min-w-0 grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7">
        {jobAreas.map((jobArea) => (
          <button
            type="button"
            key={jobArea.id}
            onClick={() => handleToggleJobArea(jobArea.id)}
            className={`flex min-h-28 min-w-0 flex-col items-center justify-center rounded-xl p-3 shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:ring-4 hover:ring-green-100 ${
              jobAreaList.includes(jobArea.id)
                ? `gradient-${jobArea.color} shadow-white shadow-md`
                : `bg-white border-[3px] ${jobArea.border}`
            }`}
          >
            <img
              src={jobArea.icon}
              alt={jobArea.name}
              className={`w-12 h-12 mb-2 ${
                jobAreaList.includes(jobArea.id) ? "filter brightness-0 invert" : ""
              }`}
            />
            <span
              className={`text-sm font-medium text-center ${
                jobAreaList.includes(jobArea.id) ? "text-white" : "text-gray-800"
              }`}
            >
              {jobArea.name}
            </span>
          </button>
        ))}
      </div>
    </FormBlock>
  );
}

export function WorkingTimeForm(props) {
  const [workingTime, setWorkingTime] = useState(props.value?.workingTimeList || []);
  const [workingTimeError, setWorkingTimeError] = useState(null);

  const handlePress = (start, end, day) => {
      let _workingTime = [...workingTime];
      const activeIndex = getActiveIndex(start, end, day);

      if (activeIndex > -1) {
          if (!_workingTime[activeIndex].id) {
              _workingTime.splice(activeIndex, 1);
          } else {
              const id = _workingTime[activeIndex].id;
              _workingTime.splice(activeIndex, 1);
              _workingTime.push({ id: id });
          }
      } else {
          _workingTime.push({ startTime: start, endTime: end, day: day });
      }

      setWorkingTime([..._workingTime]); 
  };

  useEffect(() => {
      setWorkingTime(Array.isArray(props.value?.workingTimeList) ? props.value.workingTimeList : []);
  }, [props.value]);

  const getActiveIndex = (start, end, day) => {
    return workingTime.findIndex(
      (item) => item.startTime === start && item.endTime === end && item.day === day
    );
  };

  useEffect(() => {
      setWorkingTimeError(null);
      props.onValueChange({ config: { dto: 'workDtoList' }, value: { workingTime: workingTime } });
  }, [workingTime]);

  useEffect(() => {
      if (props.error && props.error.workingTimeList) {
          setWorkingTimeError(props.error.workingTimeList);
      }
  }, [props.error]);

  return (
      <FormBlock
          {...props}
          title="Mely időszakokban szeretnél dolgozni?"
          lead="Jelöld ki a számodra megfelelő időpontokat. Akár többet is megadhatsz."
          titleColor={props.color}
      >
          <div className="w-full min-w-0 space-y-2">
              <WorkingTimeLine color={props.color} variant={props.variant} />
              <WorkingTimeLine start={6} end={14} backgroundColor={props.backgroundColor} variant={props.variant} value={workingTime} onValueChange={handlePress} />
              <WorkingTimeLine start={14} end={22} backgroundColor={props.backgroundColor} variant={props.variant} value={workingTime} onValueChange={handlePress} />
              <WorkingTimeLine start={22} end={6} backgroundColor={props.backgroundColor} variant={props.variant} value={workingTime} onValueChange={handlePress} isEnd />
          </div>

          {workingTimeError && <div className="text-red-500 text-sm mt-2">{workingTimeError}</div>}
      </FormBlock>
  );
}

function WorkingTimeLine(props) {
  const days = ['Hé', 'Ke', 'Sze', 'Csü', 'Pé', 'Szo', 'Va'];
  const isTimeRow = Number.isFinite(props.start) && Number.isFinite(props.end);

  const isActive = (day) => {
      return Array.isArray(props.value) && props.value.some(
          (item) => item.startTime === props.start && item.endTime === props.end && item.day === day
      );
  };

  return (
      <div className="grid min-w-0 grid-cols-[3.75rem_repeat(7,minmax(1.25rem,1fr))] items-center gap-px sm:grid-cols-[6.5rem_repeat(7,minmax(2.5rem,1fr))] sm:gap-2">
          {isTimeRow ? (
            <div className={`whitespace-nowrap text-center text-[9px] font-bold sm:text-xs md:text-sm ${props.variant === 'registration' ? 'text-neutral-600' : 'text-white'}`}>
              {props.start}:00 - {props.end}:00
            </div>
          ) : (
            <div aria-hidden="true" />
          )}

          {days.map((item, index) =>
              isTimeRow ? (
                <WorkingTimeButton
                  key={item}
                  isEnd={index === days.length - 1}
                  active={isActive(index + 1)}
                  backgroundColor={props.backgroundColor}
                  variant={props.variant}
                  onClick={() => props.onValueChange(props.start, props.end, index + 1)}
                />
              ) : (
                <p key={item} className={`text-center text-[9px] font-bold sm:text-xs ${props.variant === 'registration' ? 'text-neutral-600' : 'text-white'}`}>
                  {item}
                </p>
              )
          )}
      </div>
  );
}

function WorkingTimeButton(props) {
  return (
      <button
          type="button"
          className={`font-diplay place-self-center flex size-5 items-center justify-center rounded-full border text-[10px] font-black transition hover:ring-4 sm:size-9 sm:text-sm md:size-10 ${
              props.active
                ? `${props.backgroundColor} border-green-500 text-white hover:ring-green-100`
                : props.variant === 'registration'
                  ? 'border-gray-300 bg-slate-50 text-gray-400 hover:border-green-300 hover:ring-green-100'
                  : 'border-transparent bg-white/20 text-white hover:ring-white/50'
          }`}
          onClick={props.onClick}
      >
          {props.active ? '✓' : '✕'}
      </button>
  );
}

export function ContractForm(props) {
    const { getCombo } = useConfig();

    const [serialNumber, setSerialNumber] = useState(props.value && props.value.serialNumber || '-');

    const [name, setName] = useState(props.value && props.value.name || '');
    const [nameError, setNameError] = useState(null);

    const [birthDate, setBirthDate] = useState(props.value && props.value.birthDate || null);
    const [birthDateError, setBirthDateError] = useState(null);

    const [birthPlace, setBirthPlace] = useState(props.value && props.value.birthPlace || '');
    const [birthPlaceError, setBirthPlaceError] = useState(null);

    const [nationality, setNationality] = useState(props.value && props.value.nationality || null);
    const [nationalityError, setNationalityError] = useState(null);

    const [citizenshipList, setCitizenshipList] = useState(props.value?.citizenshipList?.[0] ?? "");
    const [citizenshipListError, setCitizenshipListError] = useState(null);


    const [taxResidence, setTaxResidence] = useState(props.value && props.value.taxResidence || null);
    const [taxResidenceError, setTaxResidencetError] = useState(null);


    const [bornTitle, setBornTitle] = useState(props.value && props.value.bornTitle || '');
    const [bornTitleError, setBornTitleError] = useState(null);

    const [bornLastName, setBornLastName] = useState(props.value && props.value.bornLastName || '');
    const [bornLastNameError, setBornLastNameError] = useState(null);

    const [bornFirstName, setBornFirstName] = useState(props.value && props.value.bornFirstName || '');
    const [bornFirstNameError, setBornFirstNameError] = useState(null);


    const [parentTitle, setParentTitle] = useState(props.value && props.value.parentTitle || '');
    const [parentTitleError, setParentTitleError] = useState(null);

    const [parentLastName, setParentLastName] = useState(props.value && props.value.parentLastName || '');
    const [parentLastNameError, setParentLastNameError] = useState(null);

    const [parentFirstName, setParentFirstName] = useState(props.value && props.value.parentFirstName || '');
    const [parentFirstNameError, setParentFirstNameError] = useState(null);


    const [taxId, setTaxId] = useState(props.value && props.value.taxId || '');
    const [taxIdError, setTaxIdError] = useState(null);

    const [socialSecurityNumber, setSocialSecurityNumber] = useState(props.value && props.value.socialSecurityNumber || '');
    const [socialSecurityNumberError, setSocialSecurityNumberError] = useState(null);

    const [idCardNumber, setIdCardNumber] = useState(props.value && props.value.idCardNumber || '');
    const [idCardNumberError, setIdCardNumberError] = useState(null);

    const [studentCardNumber, setStudentCardNumber] = useState(props.value && props.value.studentCardNumber || '');
    const [studentCardNumberError, setStudentCardNumberError] = useState(null);

    const [bankAccountNumber, setBankAccountNumber] = useState(props.value && props.value.bankAccountNumber || '');
    const [bankAccountNumberError, setBankAccountNumberError] = useState(null);

    const getErrorMessage = (errors, fieldName) => {
      if (!errors) return null;
      const fieldError = errors[fieldName];
      if (!fieldError) return null;

      const normalizeMessage = (e) => {
        if (!e) return null;
        return e.message ?? null;
      };

      if (Array.isArray(fieldError)) {
        const msgs = fieldError.map(normalizeMessage).filter(Boolean);
        return msgs.length > 0 ? msgs.join(" ") : null;
      }

      if (typeof fieldError === "object") {
        return normalizeMessage(fieldError);
      }

      return String(fieldError);
    };

    useEffect(() => {
      props.onValueChange?.({
        config: { dto: "dto" },
        value: {
          studentCardNumber,
          bankAccountNumber,
        },
      });
    }, [studentCardNumber, bankAccountNumber]);

    useEffect(() => {
      setNameError(getErrorMessage(props.error, 'name'));
      setBirthDateError(getErrorMessage(props.error, 'birthDate'));
      setBirthPlaceError(getErrorMessage(props.error, 'birthPlace'));
      setNationalityError(getErrorMessage(props.error, 'nationality'));
      setCitizenshipListError(getErrorMessage(props.error, 'citizenshipList'));

      setTaxResidencetError(getErrorMessage(props.error, 'taxResidence'));

      setBornTitleError(getErrorMessage(props.error, 'bornTitle'));
      setBornLastNameError(getErrorMessage(props.error, 'bornLastName'));
      setBornFirstNameError(getErrorMessage(props.error, 'bornFirstName'));

      setParentTitleError(getErrorMessage(props.error, 'parentTitle'));
      setParentLastNameError(getErrorMessage(props.error, 'parentLastName'));
      setParentFirstNameError(getErrorMessage(props.error, 'parentFirstName'));

      setTaxIdError(getErrorMessage(props.error, 'taxId'));
      setSocialSecurityNumberError(getErrorMessage(props.error, 'socialSecurityNumber'));
      setIdCardNumberError(getErrorMessage(props.error, 'idCardNumber'));
      setStudentCardNumberError(getErrorMessage(props.error, 'studentCardNumber'));
      setBankAccountNumberError(getErrorMessage(props.error, 'bankAccountNumber'));
    }, [props.error]);

    useEffect(() => { setNameError(null) }, [name]);
    useEffect(() => { setBirthDateError(null) }, [birthDate]);
    useEffect(() => { setBirthPlaceError(null) }, [birthPlace]);
    useEffect(() => { setNationalityError(null) }, [nationality]);
    useEffect(() => { setCitizenshipListError(null) }, [citizenshipList]);

    useEffect(() => { setTaxResidencetError(null) }, [taxResidence]);

    useEffect(() => { setBornTitleError(null) }, [bornTitle]);
    useEffect(() => { setBornLastNameError(null) }, [bornLastName]);
    useEffect(() => { setBornFirstNameError(null) }, [bornFirstName]);

    useEffect(() => { setParentTitleError(null) }, [parentTitle]);
    useEffect(() => { setParentLastNameError(null) }, [parentLastName]);
    useEffect(() => { setParentFirstNameError(null) }, [parentFirstName]);

    useEffect(() => { setTaxIdError(null) }, [taxId]);
    useEffect(() => { setSocialSecurityNumberError(null) }, [socialSecurityNumber]);
    useEffect(() => { setIdCardNumberError(null) }, [idCardNumber]);
    useEffect(() => { setStudentCardNumberError(null) }, [studentCardNumber]);
    useEffect(() => { setBankAccountNumberError(null) }, [bankAccountNumber]);

    return (
        <FormBlock {...props} title="Szerződéskötési adataim" lead="Az itt szereplő adatok megadása nem minősül szerződéskötésnek, csupán a szerződéskötési folyamat előkészítését segítik." titleColor={props.color}>
          <FormBlock>
            <input 
              type="text" 
              className='w-full rounded-xl px-4 py-2 lg:max-w-md'
              disabled 
              value={serialNumber} 
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="Részjegysorszámom" 
            />
            <p className="text-white text-md">Az adatot a Fürge Diák tölti ki szerződéskötéskor, és nem szerkeszthető.</p>
            <div className='form-field-grid'>
              <div className='flex flex-col gap-2'>
                <label className={`font-black text-lg md:text-sm xl:text-lg ${props.color}`} htmlFor="name">Nevem</label>
                <input 
                    id='name'
                    type="text" 
                    className={`px-4 py-2 rounded-xl ${nameError ? 'error' : ''}`} 
                    disabled
                    value={name} 
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nevem" 
                />
              </div>
              <div className='flex flex-col gap-2'>
                <label className={`font-black text-lg md:text-sm xl:text-lg ${props.color}`} htmlFor="date">Születési dátum</label>
                <input 
                    id='date'
                    type="date" 
                    className={`px-4 py-2 rounded-xl ${birthDateError ? 'error' : ''}`} 
                    disabled
                    value={birthDate ? new Date(birthDate).toISOString().split('T')[0] : ''} 
                    onChange={(e) => setBirthDate(new Date(e.target.value).toISOString())} 
                    placeholder="Születési dátumom" 
                />
              </div>
              <div className='flex flex-col gap-2'>
                <label className={`font-black text-lg md:text-sm xl:text-lg ${props.color}`} htmlFor="birthPlace">Születési hely</label>
                <input 
                    id='birthPlace'
                    type="text" 
                    className={`px-4 py-2 rounded-xl ${birthPlaceError ? 'error' : ''}`}
                    disabled
                    value={birthPlace} 
                    onChange={(e) => setBirthPlace(e.target.value)} 
                    placeholder="Születési helyem" 
                />
              </div>
              <div className='flex flex-col gap-2'>
                <label className={`font-black text-lg md:text-sm xl:text-lg ${props.color}`} htmlFor="nationality">Állampolgárságom</label>
                <select 
                  id='nationality'
                  className={`px-4 py-2 rounded-xl ${nationalityError ? 'error' : ''}`} 
                  disabled
                  value={nationality || ''} 
                  onChange={(e) => setNationality(e.target.value)}
                >
                    <option value="">Állampolgárságom</option>
                    {getCombo('country').map((country) => (
                        <option key={country.id} value={country.id}>{country.name}</option>
                    ))}
                </select>
              </div>
              <div className='flex flex-col gap-2'>
                <label className={`font-black text-lg md:text-sm xl:text-lg ${props.color}`} htmlFor="citizenshipList">További állampolgárságom</label>
                <select  
                  id='citizenshipList'
                  className={`px-4 py-2 rounded-xl ${citizenshipListError ? 'error' : ''}`} 
                  disabled
                  value={citizenshipList} 
                  onChange={(e) => setCitizenshipList([...e.target.options].filter(o => o.selected).map(o => o.value))}
                >
                    <option value="">További állampolgárságom</option>
                    {getCombo('country').map((country) => (
                        <option key={country.id} value={country.id}>{country.name}</option>
                    ))}
                </select>
              </div>
            </div>
          </FormBlock>

          <FormBlock title="Adóilletőség" lead='Amennyiben a jövedelmed 75%-át Magyarországon keresed meg, állítsd be Magyarországot! Egyéb esetben állítsd be azt az országot, ahol adózni fogsz!' titleColor={props.color}>
              <select className={`w-full rounded-xl px-4 py-2 lg:max-w-md ${taxResidenceError ? 'error' : ''}`} disabled value={taxResidence} onChange={(e) => setTaxResidence(e.target.value)}>
                  <option value="">Adóilletőségem</option>
                  {getCombo('country').map((country) => (
                      <option key={country.id} value={country.id}>{country.name}</option>
                  ))}
              </select>
          </FormBlock>

          <SchoolForm color={props.color} value={props.value} onValueChange={props.onValueChange} error={props.error} />
          <AddressForm color={props.color} value={props.value} onValueChange={props.onValueChange} error={props.error} />

          <FormBlock title="További adataim" titleColor={props.color}>
            <div className='form-field-grid'>
              <div className='flex flex-col gap-2'>
                <label className={`font-black ${props.color}`} htmlFor="taxId">Adóazonosító jelem</label>
                <input id="taxId" type="text" className={`p-2 rounded-xl ${taxIdError ? 'error' : ''}`} disabled value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="Adóazonosító jelem" />
              </div>
              <div className='flex flex-col gap-2'>
                <label className={`font-black ${props.color}`} htmlFor="socialSecurityNumber">TAJ számom</label>
                <input id="socialSecurityNumber" type="text" className={`p-2 rounded-xl ${socialSecurityNumberError ? 'error' : ''}`} disabled value={socialSecurityNumber} onChange={(e) => setSocialSecurityNumber(e.target.value)} placeholder="TAJ számom" />
              </div>
              <div className='flex flex-col gap-2'>
                <label className={`font-black ${props.color}`} htmlFor="idCardNumber">Személyi igazolvány számom</label>
                <input id="idCardNumber" type="text" className={`p-2 rounded-xl ${idCardNumberError ? 'error' : ''}`} disabled value={idCardNumber} onChange={(e) => setIdCardNumber(e.target.value)} placeholder="Személyi igazolvány számom" />
              </div>
              <div className='flex flex-col gap-2'>
                <label className={`font-black ${props.color}`} htmlFor="studentCardNumber">
                  Diákigazolvány számom
                </label>

                <input
                  id="studentCardNumber"
                  type="text"
                  className={`p-2 rounded-xl ${studentCardNumberError ? 'error' : ''}`}
                  value={studentCardNumber}
                  onChange={(e) => setStudentCardNumber(e.target.value)}
                  placeholder="Diákigazolvány számom"
                />

                {studentCardNumberError && (
                  <p className="text-red-500 text-xs mt-1">
                    {studentCardNumberError}
                  </p>
                )}
              </div>

              <div className='flex flex-col gap-2'>
                <label className={`font-black ${props.color}`} htmlFor="bankAccountNumber">
                  Bankszámla számom
                </label>

                <input
                  id="bankAccountNumber"
                  type="text"
                  className={`p-2 rounded-xl ${bankAccountNumberError ? 'error' : ''}`}
                  value={bankAccountNumber}
                  onChange={(e) => setBankAccountNumber(e.target.value)}
                  placeholder="XXXXXXXX-XXXXXXXX-XXXXXXXX"
                />

                {bankAccountNumberError && (
                  <p className="text-red-500 text-xs mt-1">
                    {bankAccountNumberError}
                  </p>
                )}
              </div>
            </div>
          </FormBlock>
        </FormBlock>
    );
}

export function DOQForm(props) {
  const { getCombo } = useConfig();

  const [doqID, setDoqID] = useState(props.value?.doqID || '');
  const [doqIDError, setDoqIDError] = useState(null);

  const [doqTermsAccepted, setDoqTermsAccepted] = useState(props.value?.doqTermsAccepted || false);
  const [doqTermsAcceptedError, setDoqTermsAcceptedError] = useState(null);

  const [type] = useState(props.value?.parentalControl || false);
  const [partner] = useState(props.value?.isPartner || false);

  useEffect(() => {
      props.onValueChange({ value: { doqID, doqTermsAccepted } });
  }, [doqID, doqTermsAccepted]);

  useEffect(() => {
      if (getErrorMessage(props.error, 'doqID')) setDoqIDError(getErrorMessage(props.error, 'doqID'));
      if (getErrorMessage(props.error, 'doqTermsAccepted')) setDoqTermsAcceptedError(getErrorMessage(props.error, 'doqTermsAccepted'));
  }, [props.error]);

  useEffect(() => { setDoqIDError(null); }, [doqID]);
  useEffect(() => { setDoqTermsAcceptedError(null); }, [doqTermsAccepted]);

  return (
      <FormBlock {...props} title='DOQ Drive' lead='Az itt megadott DOQ Drive azonosító adataiddal digitálisan, akár a telefonodról is, online tudod aláírni a munkavégzéshez szükséges papírjaidat vagy elküldeni legfrissebb jogviszonyigazolásodat. Legyen neked is digitális aláírásod akár 5 perc alatt.' titleColor={props.color}>
          <div className='form-field-grid items-center'>
            <p className='w-full text-sm text-white'>Szeretnék digitális aláírást szerezni! Mi a következő lépés és mire fogom tudni használni?</p>
            <Link to={'/digitalis-ugyintezes'} rel='noopener noreferrer' 
                  className={`${FORM_ACTION_SECONDARY} w-full lg:w-auto lg:justify-self-end`}>
              <PiInfo className="size-5" />
              További információ a digitális aláírásról
            </Link>
          </div>
          <br/>
          <br/>
          <div className='form-field-grid'>
            <div className='flex flex-col gap-2'>
              <label className={`font-black ${props.color}`} htmlFor="doqID">DOQ Drive azonosítóm</label>
              <input id="doqID" type='text' className={`w-full p-2 border rounded-xl ${doqIDError ? 'border-red-500' : 'border-gray-300'}`} disabled={props.value?.updatedDOQId} value={doqID} onChange={(e) => setDoqID(e.target.value)} placeholder='DOQ azonosítóm' />
            </div>
            <div className='flex flex-col gap-2'>
              <label className={`font-black ${props.color}`} htmlFor="idType">DOQ azonosító típusa</label>
              <input id="idType" type='text' className='w-full p-2 border border-gray-300 rounded-xl' disabled value={type ? 'Törvényes képviselő' : 'Saját'} placeholder='DOQ azonosító típusa' />
            </div>
            <div className='flex flex-col gap-2'>
              <label className={`font-black ${props.color}`} htmlFor="partner">Fürge Diák partner</label>
              <input id="partner" type='text' className='w-full p-2 border border-gray-300 rounded-xl' disabled value={partner ? 'Igen' : 'Nem'} placeholder='Fürge Diák partner' />
            </div>
          </div>
          <br/>
          <FormBlock>
              <p className='text-white text-sm'>
                Kérem a Fürge Diák Iskolaszövetkezetbe történő tagfelvételemet. Tudomásul veszem, hogy tagfelvételkor, a tagi közreműködésről szóló megállapodás kötésekor, a feladatteljesítés és a jogviszonyigazolás során lehetőségem van elektronikus aláírást használva szerződni, nyilatkozatot tenni és kötelezettséget vállalni a részemre megküldésre kerülő dokumentumok elektronikus aláírásával.
                <br/>
                <br/>
                Az elektronikus aláírásommal elfogadom a dokumentumokban foglalt valamennyi kötelezettséget, tudomásul veszem az azokban leírt tájékoztatást, továbbá hozzájárulok a személyes közreműködés után járó díjból való levonáshoz is az ott írtak szerint. A megküldésre kerülő dokumentumokban lévő valamennyi nyilatkozatot, kötelezettségvállalást egyetlen aláírással fogadom el.
                <br/>
                <br/>
                Tudomásul veszem azt is, hogy az Iskolaszövetkezet általi elektronikus aláírás a tagfelvételi kérelmem elfogadását, a megküldésre kerülő dokumentumokban lévő valamennyi nyilatkozat megtételét jelenti.
                <br/>
                <br/>
                Aláírásommal hozzájárulok diákigazolványom és/vagy a diákigazolványt helyettesítő igazolás másolatának tárolásához. Tudomásul veszem, hogy azt az Iskolaszövetkezet a saját adatbázisában adataim ellenőrzéséhez, és kedvezményekre való jogosultságának igazolásához a jogszabályi rendelkezéseknek megfelelően használja fel, de azokat más személynek nem szolgáltathatja ki. Kijelentem, hogy nappali tagozatos hallgató/tanuló vagyok, az elektronikusan aláírt okiraton szereplő adatok a valóságnak mindenben megfelelnek, a diákigazolvány és/vagy a diákigazolványt helyettesítő igazolás másolata az eredetivel egyező.
              </p>
              <br/>
              <label className='flex items-center text-white'>
                  <input type='checkbox' className='mr-2 size-4' disabled={props.value?.updatedDOQId} checked={doqTermsAccepted} onChange={(e) => setDoqTermsAccepted(e.target.checked)} />
                  A feltételeket megismertem, és elfogadom
              </label>
          </FormBlock>
          {props.value?.updatedDOQId && (
              <Link 
                to={'https://app.doqdrive.com/'} 
                target='_blank' 
                rel='noopener noreferrer' 
                className={`${FORM_ACTION_PRIMARY} w-full justify-self-start sm:w-auto`}
              >
                Belépés a DOQ Drive fiókomba
              </Link>
          )}
      </FormBlock>
  );
}

export function IncomeAccounting(props) {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState('');
  const [yearError, setYearError] = useState(null);

  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState('');
  const [monthError, setMonthError] = useState(null);

  const [documentList, setDocumentList] = useState([]);
  const { token } = useAuth();

  useEffect(() => {
      const url = global.backingsEndpoint + 'employeeUserControl/getDownloadableDocumentsList';

      axios.post(url)
          .then(response => {
              if (response.data) setDocumentList(response.data);
          })
          .catch(error => console.error(error));
  }, [token]);

  useEffect(() => {
      let yearList = [];
      let monthList = [];

      documentList.forEach(doc => {
          if (!props.monthly && !doc.month) {
              yearList.push({ name: doc.year, id: doc.id });
          } else if (props.monthly && !yearList.some(y => y.name === doc.year)) {
              yearList.push({ name: doc.year, id: doc.year });
          }
      });

      setYears(yearList);
  }, [documentList, props.monthly]);

  useEffect(() => {
      if (props.monthly) {
          setMonths(documentList.filter(doc => doc.month && doc.year === year).map(doc => ({ name: doc.month, id: doc.id })));
      }
  }, [year, documentList, props.monthly]);

  const download = () => {
      if (props.monthly) {
          if (year && month) window.open(`${global.backingsEndpoint}employeeUserControl/getUserDocumentMounthly?userDocumentId=${month}&token=${token}`, '_blank');
      } else {
          if (year) window.open(`${global.backingsEndpoint}employeeUserControl/getUserDocument?userDocumentId=${year}&token=${token}`, '_blank');
      }
  };

  return (
      <FormBlock {...props} title={props.title} lead={props.lead} titleColor={props.color}>
        <div className='form-field-grid'>
          <select className={`w-full min-w-0 rounded-xl border p-2 ${yearError ? 'border-red-500' : 'border-gray-300'}`} value={year} onChange={(e) => setYear(e.target.value)}>
              <option value="">Válaszd ki az évet!</option>
              {years.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
          </select>
          {props.mode === "monthly" && (
              <select className={`w-full min-w-0 rounded-xl border p-2 ${monthError ? 'border-red-500' : 'border-gray-300'}`} value={month} onChange={(e) => setMonth(e.target.value)}>
                  <option value="">Válaszd ki a hónapot!</option>
                  {months.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
          )}
        </div>
          <button 
            className={`${FORM_ACTION_PRIMARY} w-full sm:w-auto sm:self-end`}
            onClick={download}
          >
              Letöltés
          </button>
          <div className='mt-5 text-xs text-gray-400'>
              <p>Nincsen aktuális jövedelemigazolásod? Ennek két oka lehet:</p>
              <p>1. Nem dolgoztál a kiválasztott évben a Fürge Diák Iskolaszövetkezet szervezésében, vagy</p>
              <p>2. Nem járt még le az igazolás kiállításának határideje, így az nem készült még el.</p>
          </div>
      </FormBlock>
  );
}
